import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
  Users, Car, Home, Plane, MapPin, ChevronDown,
  Clock, CheckCircle, Send, X, AlertCircle, ArrowRight, Building,
  User, RefreshCw, Navigation, Eye, Edit2, Copy
} from 'lucide-react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { format, subDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

// Common airport coordinates lookup
const AIRPORT_COORDINATES = {
  // Poland
  'WAW': { lat: 52.1657, lng: 20.9671, name: 'Warsaw Chopin Airport' },
  'WMI': { lat: 52.4511, lng: 20.6517, name: 'Warsaw Modlin Airport' },
  'KRK': { lat: 50.0777, lng: 19.7848, name: 'Kraków Airport' },
  'GDN': { lat: 54.3776, lng: 18.4662, name: 'Gdańsk Airport' },
  'KTW': { lat: 50.4743, lng: 19.0800, name: 'Katowice Airport' },
  'WRO': { lat: 51.1027, lng: 16.8858, name: 'Wrocław Airport' },
  'POZ': { lat: 52.4211, lng: 16.8263, name: 'Poznań Airport' },
  'RZE': { lat: 50.1100, lng: 22.0190, name: 'Rzeszów Airport' },
  'SZZ': { lat: 53.5847, lng: 14.9022, name: 'Szczecin Airport' },
  'LUZ': { lat: 51.2403, lng: 22.7134, name: 'Lublin Airport' },
  'BZG': { lat: 53.0968, lng: 17.9777, name: 'Bydgoszcz Airport' },
  'LCJ': { lat: 51.7219, lng: 19.3981, name: 'Łódź Airport' },
  // Germany
  'FRA': { lat: 50.0379, lng: 8.5622, name: 'Frankfurt Airport' },
  'MUC': { lat: 48.3538, lng: 11.7861, name: 'Munich Airport' },
  'BER': { lat: 52.3667, lng: 13.5033, name: 'Berlin Brandenburg Airport' },
  'DUS': { lat: 51.2895, lng: 6.7668, name: 'Düsseldorf Airport' },
  'HAM': { lat: 53.6304, lng: 10.0063, name: 'Hamburg Airport' },
  'CGN': { lat: 50.8659, lng: 7.1427, name: 'Cologne Bonn Airport' },
  'STR': { lat: 48.6899, lng: 9.2220, name: 'Stuttgart Airport' },
  'HAJ': { lat: 52.4611, lng: 9.6850, name: 'Hannover Airport' },
  'NUE': { lat: 49.4987, lng: 11.0780, name: 'Nuremberg Airport' },
  'LEJ': { lat: 51.4324, lng: 12.2416, name: 'Leipzig Airport' },
  'BRE': { lat: 53.0475, lng: 8.7867, name: 'Bremen Airport' },
  'DTM': { lat: 51.5183, lng: 7.6122, name: 'Dortmund Airport' },
  // Netherlands
  'AMS': { lat: 52.3105, lng: 4.7683, name: 'Amsterdam Schiphol' },
  'EIN': { lat: 51.4500, lng: 5.3744, name: 'Eindhoven Airport' },
  'RTM': { lat: 51.9569, lng: 4.4372, name: 'Rotterdam Airport' },
  // Belgium
  'BRU': { lat: 50.9014, lng: 4.4844, name: 'Brussels Airport' },
  'CRL': { lat: 50.4592, lng: 4.4538, name: 'Brussels South Charleroi' },
  // France
  'CDG': { lat: 49.0097, lng: 2.5479, name: 'Paris Charles de Gaulle' },
  'ORY': { lat: 48.7233, lng: 2.3794, name: 'Paris Orly' },
  // UK
  'LHR': { lat: 51.4700, lng: -0.4543, name: 'London Heathrow' },
  'LGW': { lat: 51.1537, lng: -0.1821, name: 'London Gatwick' },
  'STN': { lat: 51.8860, lng: 0.2389, name: 'London Stansted' },
  'MAN': { lat: 53.3537, lng: -2.2750, name: 'Manchester Airport' },
  'EDI': { lat: 55.9508, lng: -3.3615, name: 'Edinburgh Airport' },
  // Spain
  'MAD': { lat: 40.4983, lng: -3.5676, name: 'Madrid Barajas' },
  'BCN': { lat: 41.2971, lng: 2.0785, name: 'Barcelona El Prat' },
  // Italy
  'FCO': { lat: 41.8003, lng: 12.2389, name: 'Rome Fiumicino' },
  'MXP': { lat: 45.6306, lng: 8.7281, name: 'Milan Malpensa' },
  // Austria
  'VIE': { lat: 48.1103, lng: 16.5697, name: 'Vienna Airport' },
  // Switzerland
  'ZRH': { lat: 47.4647, lng: 8.5492, name: 'Zurich Airport' },
  'GVA': { lat: 46.2381, lng: 6.1089, name: 'Geneva Airport' },
  // Czech Republic
  'PRG': { lat: 50.1008, lng: 14.2600, name: 'Prague Airport' },
  // Denmark
  'CPH': { lat: 55.6180, lng: 12.6508, name: 'Copenhagen Airport' },
  // Sweden
  'ARN': { lat: 59.6519, lng: 17.9186, name: 'Stockholm Arlanda' },
  // Norway
  'OSL': { lat: 60.1976, lng: 11.1004, name: 'Oslo Gardermoen' },
  // Finland
  'HEL': { lat: 60.3172, lng: 24.9633, name: 'Helsinki Airport' },
};

// Get airport coordinates by code
const getAirportCoordinates = (code) => {
  if (!code) return null;
  const upperCode = code.toUpperCase().trim();
  return AIRPORT_COORDINATES[upperCode] || null;
};

// Calculate recommended departure time for airport dropoffs
// Logic: Car must arrive at airport 2h before earliest flight
// Departure time = (earliest flight - 2h) - route duration - 30min buffer
// Rounded down to nearest half hour (always adding more time)
const calculateRecommendedDropoffTime = (earliestDeparture, routeDurationMinutes = 0) => {
  if (!earliestDeparture) return null;

  const [hours, minutes] = earliestDeparture.split(':').map(Number);

  // Convert to total minutes
  let totalMinutes = hours * 60 + minutes;

  // Subtract 2h (must be at airport 2h before flight)
  totalMinutes -= 120;

  // Subtract route duration
  totalMinutes -= routeDurationMinutes;

  // Subtract 30min buffer
  totalMinutes -= 30;

  // Round down to nearest half hour (giving more time)
  // e.g., 10:45 -> 10:30, 10:29 -> 10:00
  const roundedMinutes = Math.floor(totalMinutes / 30) * 30;

  // Handle negative times (very early morning flights)
  const finalMinutes = Math.max(0, roundedMinutes);

  const resultHours = Math.floor(finalMinutes / 60) % 24;
  const resultMins = finalMinutes % 60;

  return `${resultHours.toString().padStart(2, '0')}:${resultMins.toString().padStart(2, '0')}`;
};

// Round coordinate to 6 decimal places (API requirement)
const roundCoord = (value) => {
  if (value === null || value === undefined) return null;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return isNaN(num) ? null : parseFloat(num.toFixed(6));
};

// Map icon creators
const createProjectIcon = () => {
  const size = 40;
  return L.divIcon({
    className: 'custom-project-marker',
    html: `<div style="
      background-color: #059669;
      color: white;
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      border: 3px solid white;
      box-shadow: 0 3px 6px rgba(0,0,0,0.4);
    ">🏗️</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
};

const createAirportIcon = () => {
  const size = 40;
  return L.divIcon({
    className: 'custom-airport-marker',
    html: `<div style="
      background-color: #2563eb;
      color: white;
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      border: 3px solid white;
      box-shadow: 0 3px 6px rgba(0,0,0,0.4);
    ">✈️</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
};

const createAccommodationIcon = (number, isSelected = false) => {
  const size = 32;
  const color = isSelected ? '#7c3aed' : '#059669';
  // Show home icon for overview mode (when number is null), otherwise show number
  const content = number === null ? '🏠' : number;
  const fontSize = number === null ? '16px' : '14px';
  return L.divIcon({
    className: 'custom-accommodation-marker',
    html: `<div style="
      background-color: ${color};
      color: white;
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: ${fontSize};
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    ">${content}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
};

// Component to fit map bounds to markers
const MapFitBounds = ({ positions }) => {
  const map = useMap();

  useEffect(() => {
    if (positions && positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
    }
  }, [map, positions]);

  return null;
};

// Drag types
const DRAG_TYPES = {
  PERSON: 'person',
  CAR: 'car',
  TRANSPORT: 'transport'
};

// Main tabs
const MAIN_TABS = {
  DAILY_PLANNING: 'daily_planning',
  AIRPORT_TRANSFERS: 'airport_transfers'
};

// Sub tabs for Daily Planning
const DAILY_SUB_TABS = {
  TO_PROJECT: 'to_project',
  TO_ACCOMMODATION: 'to_accommodation'
};

// Sub tabs for Airport Transfers
const AIRPORT_SUB_TABS = {
  PICKUPS: 'pickups',
  DROPOFFS: 'dropoffs'
};

// Legacy mapping for backward compatibility
const TRANSPORT_TABS = {
  AIRPORT_PICKUPS: 'airport_pickups',
  DAILY_ROUTES: 'daily_routes',
  AIRPORT_DROPOFFS: 'airport_dropoffs'
};

// Draggable Person Card for pending transport
const DraggablePerson = ({ person, type, isAssigned = false, accommodation = null, projectName = null, selectedProject = null, onNavigateToAccommodation = null }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: DRAG_TYPES.PERSON,
    item: { type: DRAG_TYPES.PERSON, person, transportType: type },
    canDrag: !isAssigned,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [person, type, isAssigned]);

  const getDisplayName = () => {
    if (person.first_name && person.last_name) {
      return `${person.first_name} ${person.last_name}`;
    }
    return person.user_name || person.username || 'Unknown';
  };

  const isPickup = type === 'airport_pickup';
  const isDropoff = type === 'airport_dropoff';
  const isAirportTransfer = isPickup || isDropoff;
  const isDailyToProject = type === 'daily_to_work';
  const isDailyToAccommodation = type === 'daily_to_home';
  const isDailyRoute = isDailyToProject || isDailyToAccommodation;

  // Build flight tooltip
  const getFlightTooltip = () => {
    const parts = [];
    if (person.flight_number) parts.push(`Flight: ${person.flight_number}`);
    if (person.departure_airport && person.arrival_airport) {
      parts.push(`Route: ${person.departure_airport} → ${person.arrival_airport}`);
    }
    if (person.departure_time) parts.push(`Departure: ${person.departure_time.slice(0,5)}`);
    if (person.arrival_time) parts.push(`Arrival: ${person.arrival_time.slice(0,5)}`);
    return parts.join('\n');
  };

  // Build accommodation tooltip with full address
  const getAccommodationTooltip = () => {
    if (!accommodation) return 'No accommodation assigned';
    const parts = [accommodation.accommodation_name];
    if (accommodation.room_name) parts.push(`Room: ${accommodation.room_name}`);
    // Build full address
    const addressParts = [];
    if (accommodation.address) addressParts.push(accommodation.address);
    if (accommodation.city) addressParts.push(accommodation.city);
    if (accommodation.post_code) addressParts.push(accommodation.post_code);
    if (accommodation.country) addressParts.push(accommodation.country);
    if (addressParts.length > 0) {
      parts.push(`Address: ${addressParts.join(', ')}`);
    }
    if (accommodation.phone) parts.push(`Phone: ${accommodation.phone}`);
    return parts.join('\n');
  };

  // For airport transfers, show two-column layout: Person | Route
  if (isAirportTransfer) {
    return (
      <div
        ref={drag}
        className={`border rounded transition-all text-xs ${
          isAssigned
            ? 'bg-steel-100 border-steel-300 opacity-50 cursor-not-allowed'
            : 'bg-white border-steel-200 cursor-move hover:shadow-sm'
        } ${isDragging ? 'opacity-50' : ''}`}
      >
        <div className="flex">
          {/* Left half: Person info */}
          <div className="w-1/2 p-2 border-r border-steel-100">
            <div className="flex items-center space-x-2">
              <div className="w-7 h-7 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-3.5 h-3.5 text-brand-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900 truncate">{getDisplayName()}</div>
                {person.flight_number && (
                  <div
                    className="text-[10px] text-slate-500 flex items-center cursor-help"
                    title={getFlightTooltip()}
                  >
                    <Plane className="w-2.5 h-2.5 mr-0.5 flex-shrink-0" />
                    <span className="truncate">{person.flight_number}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right half: Route (From → To) */}
          <div className="w-1/2 p-2 bg-steel-50 flex items-center justify-between">
            {isPickup ? (
              <>
                {/* Pickup: Airport → Accommodation */}
                <div className="text-center flex-1">
                  <div className="font-medium text-green-700 text-[11px]">
                    {person.arrival_airport || '???'}
                  </div>
                  {person.arrival_time && (
                    <div className="text-[10px] text-green-600">{person.arrival_time.slice(0,5)}</div>
                  )}
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 mx-1 flex-shrink-0" />
                <div
                  className="text-center flex-1 cursor-help"
                  title={getAccommodationTooltip()}
                >
                  {accommodation ? (
                    <>
                      <div className="font-medium text-purple-700 truncate text-[11px]">
                        {accommodation.accommodation_name}
                      </div>
                      {accommodation.room_name && (
                        <div className="text-[10px] text-purple-500">Room {accommodation.room_name}</div>
                      )}
                    </>
                  ) : (
                    <div
                      className={`text-[10px] text-yellow-600 flex items-center justify-center ${onNavigateToAccommodation ? 'cursor-pointer hover:text-yellow-800 hover:underline' : ''}`}
                      onClick={(e) => {
                        if (onNavigateToAccommodation) {
                          e.stopPropagation();
                          onNavigateToAccommodation();
                        }
                      }}
                      title={onNavigateToAccommodation ? 'Click to assign accommodation' : 'No accommodation assigned'}
                    >
                      <AlertCircle className="w-2.5 h-2.5 mr-0.5" />
                      No Accommodation
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Dropoff: Accommodation → Airport */}
                <div
                  className="text-center flex-1 cursor-help"
                  title={getAccommodationTooltip()}
                >
                  {accommodation ? (
                    <>
                      <div className="font-medium text-purple-700 truncate text-[11px]">
                        {accommodation.accommodation_name}
                      </div>
                      {accommodation.room_name && (
                        <div className="text-[10px] text-purple-500">Room {accommodation.room_name}</div>
                      )}
                    </>
                  ) : (
                    <div
                      className={`text-[10px] text-yellow-600 flex items-center justify-center ${onNavigateToAccommodation ? 'cursor-pointer hover:text-yellow-800 hover:underline' : ''}`}
                      onClick={(e) => {
                        if (onNavigateToAccommodation) {
                          e.stopPropagation();
                          onNavigateToAccommodation();
                        }
                      }}
                      title={onNavigateToAccommodation ? 'Click to assign accommodation' : 'No accommodation assigned'}
                    >
                      <AlertCircle className="w-2.5 h-2.5 mr-0.5" />
                      No Accommodation
                    </div>
                  )}
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 mx-1 flex-shrink-0" />
                <div className="text-center flex-1">
                  <div className="font-medium text-orange-700 text-[11px]">
                    {person.departure_airport || '???'}
                  </div>
                  {person.departure_time && (
                    <div className="text-[10px] text-orange-600">{person.departure_time.slice(0,5)}</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Layout for daily routes (to project / to accommodation)
  if (isDailyRoute) {
    return (
      <div
        ref={drag}
        className={`border rounded transition-all text-xs ${
          isAssigned
            ? 'bg-steel-100 border-steel-300 opacity-50 cursor-not-allowed'
            : 'bg-white border-steel-200 cursor-move hover:shadow-sm'
        } ${isDragging ? 'opacity-50' : ''}`}
      >
        <div className="flex">
          {/* Left half: Person info */}
          <div className="w-1/2 p-2 border-r border-steel-100">
            <div className="flex items-center space-x-2">
              <div className="w-7 h-7 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-3.5 h-3.5 text-brand-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900 truncate">{getDisplayName()}</div>
              </div>
            </div>
          </div>

          {/* Right half: Route (From → To) */}
          <div className="w-1/2 p-2 bg-steel-50 flex items-center justify-between">
            {isDailyToProject ? (
              <>
                {/* To Project: Accommodation → Project */}
                <div
                  className="text-center flex-1 cursor-help"
                  title={getAccommodationTooltip()}
                >
                  {accommodation ? (
                    <>
                      <div className="font-medium text-purple-700 truncate text-[11px]">
                        {accommodation.accommodation_name}
                      </div>
                      {accommodation.room_name && (
                        <div className="text-[10px] text-purple-500">Room {accommodation.room_name}</div>
                      )}
                    </>
                  ) : (
                    <div
                      className={`text-[10px] text-yellow-600 flex items-center justify-center ${onNavigateToAccommodation ? 'cursor-pointer hover:text-yellow-800 hover:underline' : ''}`}
                      onClick={(e) => {
                        if (onNavigateToAccommodation) {
                          e.stopPropagation();
                          onNavigateToAccommodation();
                        }
                      }}
                      title={onNavigateToAccommodation ? 'Click to assign accommodation' : 'No accommodation assigned'}
                    >
                      <AlertCircle className="w-2.5 h-2.5 mr-0.5" />
                      No Accommodation
                    </div>
                  )}
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 mx-1 flex-shrink-0" />
                <div className="text-center flex-1">
                  <div className="font-medium text-green-700 truncate text-[11px]">
                    {projectName || 'Project'}
                  </div>
                  <div className="text-[10px] text-green-500">Office</div>
                </div>
              </>
            ) : (
              <>
                {/* To Accommodation: Project → Accommodation */}
                <div className="text-center flex-1">
                  <div className="font-medium text-green-700 truncate text-[11px]">
                    {projectName || 'Project'}
                  </div>
                  <div className="text-[10px] text-green-500">Office</div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 mx-1 flex-shrink-0" />
                <div
                  className="text-center flex-1 cursor-help"
                  title={getAccommodationTooltip()}
                >
                  {accommodation ? (
                    <>
                      <div className="font-medium text-purple-700 truncate text-[11px]">
                        {accommodation.accommodation_name}
                      </div>
                      {accommodation.room_name && (
                        <div className="text-[10px] text-purple-500">Room {accommodation.room_name}</div>
                      )}
                    </>
                  ) : (
                    <div
                      className={`text-[10px] text-yellow-600 flex items-center justify-center ${onNavigateToAccommodation ? 'cursor-pointer hover:text-yellow-800 hover:underline' : ''}`}
                      onClick={(e) => {
                        if (onNavigateToAccommodation) {
                          e.stopPropagation();
                          onNavigateToAccommodation();
                        }
                      }}
                      title={onNavigateToAccommodation ? 'Click to assign accommodation' : 'No accommodation assigned'}
                    >
                      <AlertCircle className="w-2.5 h-2.5 mr-0.5" />
                      No Accommodation
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default layout (fallback)
  return (
    <div
      ref={drag}
      className={`border rounded px-2 py-1.5 transition-all text-xs ${
        isAssigned
          ? 'bg-steel-100 border-steel-300 opacity-50 cursor-not-allowed'
          : 'bg-white border-steel-200 cursor-move hover:shadow-sm'
      } ${isDragging ? 'opacity-50' : ''}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="w-6 h-6 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0">
            <User className="w-3 h-3 text-brand-600" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate">{getDisplayName()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Draggable Car Component
const DraggableCar = ({ car, passengers = [], onRemovePassenger, onPersonDrop, availablePeople = [], userAccommodations = {} }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: DRAG_TYPES.CAR,
    item: { type: DRAG_TYPES.CAR, car, passengers },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [car, passengers]);

  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: [DRAG_TYPES.PERSON],
    canDrop: () => {
      // Check if car has available seats
      const maxSeats = car.number_of_seats || 5;
      return passengers.length < maxSeats;
    },
    drop: (item) => {
      // Handle drop of person onto car
      if (onPersonDrop && item.person) {
        onPersonDrop(item.person, car);
      }
      return { car };
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [car, passengers, onPersonDrop]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter out passengers already in this car and check seat availability
  const maxSeats = car.number_of_seats || 5;
  const hasAvailableSeats = passengers.length < maxSeats;
  const passengerIds = passengers.map(p => p.id || p.user_id);
  const filteredAvailablePeople = availablePeople.filter(p => {
    const personId = p.id || p.user_id;
    return !passengerIds.includes(personId);
  });

  const handleSelectPerson = (person) => {
    if (onPersonDrop && hasAvailableSeats) {
      onPersonDrop(person, car);
      // Close dropdown only if car will be full after adding this person
      const seatsAfterAdd = passengers.length + 1;
      if (seatsAfterAdd >= maxSeats) {
        setShowDropdown(false);
      }
      // Otherwise keep dropdown open for selecting more people
    }
  };

  return (
    <div
      ref={(node) => drag(drop(node))}
      className={`bg-white px-2 py-1.5 transition-all text-xs ${
        isDragging ? 'opacity-50' : ''
      } ${isOver && canDrop ? 'bg-brand-50' : ''}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-1.5">
          <Car className={`w-3.5 h-3.5 ${passengers.length > 0 ? 'text-green-600' : 'text-slate-500'}`} />
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate text-xs">
              {car.car_brand} {car.car_model}
            </div>
            <div className="text-[10px] text-slate-500">{car.plate_number}</div>
          </div>
        </div>
        <div className="text-[10px] bg-steel-100 px-1.5 py-0.5 rounded-full">
          {passengers.length}/{maxSeats}
        </div>
      </div>

      {passengers.length > 0 && (
        <div className="mt-1.5 pt-1.5 border-t border-steel-200 space-y-0.5">
          {passengers.map((passenger, index) => (
            <div key={passenger.id || index} className={`text-[10px] px-1.5 py-0.5 rounded flex items-center justify-between ${
              index === 0 ? 'bg-green-100 text-green-800' : 'bg-brand-100 text-brand-800'
            }`}>
              <span className="truncate">
                {index === 0 ? '🚗 ' : '👥 '}
                {passenger.first_name || passenger.user_name || 'Unknown'}
              </span>
              {onRemovePassenger && (
                <button onClick={() => onRemovePassenger(passenger)} className="ml-1 text-red-500 hover:text-red-700">
                  <X className="w-2.5 h-2.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Drop zone with dropdown - always visible */}
      <div className="relative mt-1" ref={dropdownRef}>
        <div
          onClick={(e) => {
            e.stopPropagation();
            if (hasAvailableSeats && filteredAvailablePeople.length > 0) {
              setShowDropdown(!showDropdown);
            }
          }}
          className={`text-[10px] text-center py-1 border border-dashed rounded cursor-pointer flex items-center justify-center gap-1 ${
            isOver && canDrop
              ? 'border-brand-400 text-brand-600 bg-brand-50'
              : hasAvailableSeats && filteredAvailablePeople.length > 0
                ? 'border-steel-300 text-slate-500 hover:border-brand-400 hover:text-brand-600 hover:bg-brand-50'
                : 'border-steel-200 text-slate-300'
          }`}
        >
          {isOver && canDrop ? (
            'Drop here'
          ) : hasAvailableSeats && filteredAvailablePeople.length > 0 ? (
            <>
              <Users className="w-3 h-3" />
              <span>Add person ({filteredAvailablePeople.length})</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
            </>
          ) : !hasAvailableSeats ? (
            'Car full'
          ) : (
            'No people available'
          )}
        </div>

        {/* Dropdown list */}
        {showDropdown && filteredAvailablePeople.length > 0 && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-steel-200 rounded-lg shadow-lg max-h-[28rem] overflow-y-auto">
            {filteredAvailablePeople.map((person) => {
              const personId = person.user_id || person.id;
              const accInfo = userAccommodations[personId];
              // Format time as HH:MM and get airport
              const timeRaw = person.arrival_time || person.departure_time || '';
              const timeFormatted = timeRaw ? timeRaw.substring(0, 5) : '';
              const airport = person.arrival_airport || person.departure_airport || '';
              return (
                <div
                  key={personId}
                  onClick={() => handleSelectPerson(person)}
                  className="px-2 py-1.5 hover:bg-brand-50 cursor-pointer border-b border-steel-100 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 bg-brand-100 rounded-full flex items-center justify-center">
                        <User className="w-3 h-3 text-brand-600" />
                      </div>
                      <div>
                        <div className="text-[10px] font-medium text-slate-900">
                          {person.first_name || person.user_name || 'Unknown'}
                        </div>
                        {accInfo && (
                          <div className="text-[9px] text-slate-500 flex items-center gap-0.5">
                            <Home className="w-2.5 h-2.5" />
                            {accInfo.accommodation_name}
                          </div>
                        )}
                      </div>
                    </div>
                    {(timeFormatted || airport) && (
                      <div className="text-[9px] text-slate-400 flex items-center gap-1">
                        {airport && <span className="font-medium text-brand-500">{airport}</span>}
                        {timeFormatted && <span>{timeFormatted}</span>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Transport Card - shows assigned transport
const TransportCard = ({ transport, onEdit, onDelete, onSendEmail, onSelect, isSelected }) => {
  const getTypeIcon = () => {
    switch (transport.transport_type) {
      case 'airport_pickup': return <Plane className="w-3 h-3 text-green-600" />;
      case 'airport_dropoff': return <Plane className="w-3 h-3 text-orange-600 transform rotate-45" />;
      case 'daily_to_work': return <ArrowRight className="w-3 h-3 text-brand-600" />;
      case 'daily_from_work': return <ArrowRight className="w-3 h-3 text-purple-600 transform rotate-180" />;
      default: return <Car className="w-3 h-3 text-slate-600" />;
    }
  };

  const getStatusColor = () => {
    switch (transport.status) {
      case 'scheduled': return 'bg-brand-100 text-brand-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-steel-100 text-slate-800';
    }
  };

  const isMultiStop = transport.route_sequence && transport.notes?.includes('Multi-stop');

  return (
    <div
      onClick={() => onSelect && onSelect(transport)}
      className={`bg-white border rounded px-2 py-1.5 text-xs cursor-pointer transition-all ${
        isSelected
          ? 'border-brand-500 ring-2 ring-brand-200 bg-brand-50'
          : isMultiStop
            ? 'border-purple-300 hover:border-purple-400'
            : 'border-steel-200 hover:border-steel-300'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 min-w-0">
          {getTypeIcon()}
          {isMultiStop && (
            <span className="text-[10px] px-1 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">
              #{transport.route_sequence}
            </span>
          )}
          <span className="font-medium">
            {transport.scheduled_time?.slice(0, 5)}
            {transport.scheduled_end_time && (
              <span className="text-slate-400">→{transport.scheduled_end_time?.slice(0, 5)}</span>
            )}
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${getStatusColor()}`}>{transport.status}</span>
        </div>
        <div className="flex items-center space-x-1 flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit && onEdit(transport);
            }}
            className="p-0.5 rounded text-slate-600 hover:bg-steel-100"
            title="Edit transport"
          >
            <Edit2 className="w-3 h-3" />
          </button>
          {!transport.email_sent && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSendEmail && onSendEmail(transport);
              }}
              className="p-0.5 text-brand-600 hover:bg-brand-50 rounded"
              title="Send email"
            >
              <Send className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete && onDelete(transport);
            }}
            className="p-0.5 text-red-600 hover:bg-red-50 rounded"
            title="Delete"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
      {/* Route info - use waypoints-based origin/destination */}
      {transport.waypoints && transport.waypoints.length > 0 && (
        <div className="mt-1 text-slate-600 flex items-center truncate">
          <span className="truncate">{transport.waypoints[0]?.name || transport.origin?.name || '-'}</span>
          <ArrowRight className="w-3 h-3 mx-1 flex-shrink-0 text-slate-400" />
          <span className="truncate">{transport.waypoints[transport.waypoints.length - 1]?.name || transport.destination?.name || '-'}</span>
          {transport.waypoints.length > 2 && (
            <span className="ml-1 text-[10px] text-slate-400">({transport.waypoints.length - 2} stops)</span>
          )}
        </div>
      )}
      {/* Car and passengers */}
      {transport.car_data && (
        <div className="mt-1 pt-1 border-t border-steel-100 flex items-center flex-wrap gap-1">
          <span className="text-slate-500 flex items-center">
            <Car className="w-2.5 h-2.5 mr-0.5" />
            {transport.car_data.plate_number}
          </span>
          {transport.passengers_data?.map((p, i) => (
            <span key={p.id} className={`text-[10px] px-1 py-0.5 rounded ${i === 0 ? 'bg-green-100 text-green-800' : 'bg-brand-100 text-brand-800'}`}>
              {i === 0 ? '🚗' : '👥'} {p.first_name}
            </span>
          ))}
        </div>
      )}

    </div>
  );
};

// Drop Zone for creating new transport
const TransportDropZone = ({ onDrop, label, icon: Icon, className = "" }) => {
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: [DRAG_TYPES.CAR],
    drop: (item) => {
      onDrop(item);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }));

  return (
    <div
      ref={drop}
      className={`border border-dashed rounded px-3 py-2 text-center transition-all flex items-center justify-center space-x-2 ${
        isOver && canDrop ? 'border-brand-400 bg-brand-50' : 'border-steel-300 hover:border-gray-400'
      } ${className}`}
    >
      <Icon className={`w-4 h-4 ${isOver && canDrop ? 'text-brand-600' : 'text-slate-400'}`} />
      <span className={`text-xs ${isOver && canDrop ? 'text-brand-600 font-medium' : 'text-slate-500'}`}>
        {isOver && canDrop ? 'Drop here' : label}
      </span>
    </div>
  );
};

// Main Component - Daily view for a single day
const DailyPlanning1Day = ({ selectedDate, selectedProject, projects, onClose }) => {
  const { user } = useContext(UserContext);
  const companyId = user?.company?.id;
  const navigate = useNavigate();

  // Handler to navigate to accommodation page with current project
  const handleNavigateToAccommodation = () => {
    navigate(`/daily-task-coordination/accommodation?project=${selectedProject}`);
  };

  // State
  const [loading, setLoading] = useState(true);

  // Tab state - new structure
  const [mainTab, setMainTab] = useState(MAIN_TABS.DAILY_PLANNING);
  const [dailySubTab, setDailySubTab] = useState(DAILY_SUB_TABS.TO_PROJECT);
  const [airportSubTab, setAirportSubTab] = useState(AIRPORT_SUB_TABS.PICKUPS);

  // Computed activeTab for backward compatibility with existing logic
  const activeTab = useMemo(() => {
    if (mainTab === MAIN_TABS.AIRPORT_TRANSFERS) {
      return airportSubTab === AIRPORT_SUB_TABS.PICKUPS
        ? TRANSPORT_TABS.AIRPORT_PICKUPS
        : TRANSPORT_TABS.AIRPORT_DROPOFFS;
    }
    return TRANSPORT_TABS.DAILY_ROUTES;
  }, [mainTab, airportSubTab]);

  // Data - projects are passed as props
  const [users, setUsers] = useState([]);
  const [cars, setCars] = useState([]);
  const [accommodations, setAccommodations] = useState([]);
  const [localTransports, setLocalTransports] = useState([]);

  // All users and cars (for reference lookup)
  const [allUsers, setAllUsers] = useState([]);
  const [allCars, setAllCars] = useState([]);

  // Pending transport needs - Airport Transfers
  const [pendingPickups, setPendingPickups] = useState([]);
  const [pendingDropoffs, setPendingDropoffs] = useState([]);

  // Pending transport needs - Daily Planning
  const [pendingToProject, setPendingToProject] = useState([]);
  const [pendingToAccommodation, setPendingToAccommodation] = useState([]);

  // Refresh trigger - increment to force data reload
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Recent transports (for "Copy from recent day" feature) - last 7 days
  const [recentTransports, setRecentTransports] = useState([]);

  // Car assignments (in-progress, not yet saved) - separate for pickups and dropoffs
  const [carAssignments, setCarAssignments] = useState({
    pickups: {},   // { carId: [passengers] }
    dropoffs: {}   // { carId: [passengers] }
  });
  const [carPickupTimes, setCarPickupTimes] = useState({
    pickups: {},   // { carId: 'HH:mm' }
    dropoffs: {}   // { carId: 'HH:mm' }
  });
  const [carEndTimes, setCarEndTimes] = useState({
    pickups: {},   // { carId: 'HH:mm' }
    dropoffs: {}   // { carId: 'HH:mm' }
  });
  // Track manually set end times (to avoid overwriting admin's manual input)
  const [manualEndTimes, setManualEndTimes] = useState({
    pickups: {},   // { carId: true/false }
    dropoffs: {}   // { carId: true/false }
  });

  // Car assignments for Daily Planning - separate for to_project and to_accommodation
  const [dailyCarAssignments, setDailyCarAssignments] = useState({
    to_project: {},      // { carId: [passengers] }
    to_accommodation: {} // { carId: [passengers] }
  });
  const [dailyCarPickupTimes, setDailyCarPickupTimes] = useState({
    to_project: {},      // { carId: 'HH:mm' } - default 07:00
    to_accommodation: {} // { carId: 'HH:mm' } - default 16:00
  });
  const [dailyCarEndTimes, setDailyCarEndTimes] = useState({
    to_project: {},      // { carId: 'HH:mm' }
    to_accommodation: {} // { carId: 'HH:mm' }
  });

  // Route optimization state
  const [optimizedRoutes, setOptimizedRoutes] = useState({
    pickups: {},   // { carId: { geometry, segments, stops, total_duration_minutes, ... } }
    dropoffs: {}   // { carId: { geometry, segments, stops, total_duration_minutes, ... } }
  });
  const [dailyOptimizedRoutes, setDailyOptimizedRoutes] = useState({
    to_project: {},      // { carId: { geometry, segments, stops, total_duration_minutes, ... } }
    to_accommodation: {} // { carId: { geometry, segments, stops, total_duration_minutes, ... } }
  });
  const [routeOptimizing, setRouteOptimizing] = useState({}); // { carId: true/false }

  // Refs for debouncing route optimization
  const optimizeDebounceTimers = useRef({}); // { `${tabKey}_${carId}`: timeoutId }
  const prevAccommodationSets = useRef({}); // { `${tabKey}_${carId}`: Set of accommodation_ids }

  const [activeCarId, setActiveCarId] = useState(null); // Currently selected car for map display
  const [viewingTransportId, setViewingTransportId] = useState(null); // Transport being viewed on map
  const [editingTransportId, setEditingTransportId] = useState(null); // Transport being edited

  // Room assignments - maps user_id to their accommodation info
  const [userAccommodations, setUserAccommodations] = useState({}); // { user_id: { accommodation_id, accommodation_name, room_name } }

  // Headers for API calls
  const getHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
    'Content-Type': 'application/json'
  });

  // Load initial data (all users for reference, all cars for reference)
  // Projects are passed as props from parent
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const headers = getHeaders();

        const [usersRes, carsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/users/`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_BASE_URL}/api/cars/`, { headers }).catch(() => ({ data: [] }))
        ]);

        setAllUsers(usersRes.data);
        setAllCars(carsRes.data);

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Load transport data and project-specific resources for selected date and project
  useEffect(() => {
    const fetchTransportData = async () => {
      if (!selectedProject) {
        setUsers([]);
        setCars([]);
        return;
      }

      try {
        const headers = getHeaders();
        const dateStr = format(selectedDate, 'yyyy-MM-dd');

        // Fetch all data in parallel
        const [transportsRes, pickupsRes, dropoffsRes, toProjectRes, toAccommodationRes, userAssignmentsRes, carAssignmentsRes, accommodationsRes, roomAssignmentsRes, yesterdayTransportsRes] = await Promise.all([
          // Fetch local transports
          axios.get(`${API_BASE_URL}/api/local-transports/`, {
            headers,
            params: { project: selectedProject, date: dateStr }
          }).catch(() => ({ data: [] })),

          // Fetch pending airport pickups
          axios.get(`${API_BASE_URL}/api/local-transports/pending_airport_pickups/`, {
            headers,
            params: { project: selectedProject, start_date: dateStr, end_date: dateStr }
          }).catch(() => ({ data: [] })),

          // Fetch pending airport dropoffs
          axios.get(`${API_BASE_URL}/api/local-transports/pending_airport_dropoffs/`, {
            headers,
            params: { project: selectedProject, start_date: dateStr, end_date: dateStr }
          }).catch(() => ({ data: [] })),

          // Fetch pending daily to project (morning route)
          axios.get(`${API_BASE_URL}/api/local-transports/pending_daily_to_project/`, {
            headers,
            params: { project: selectedProject, start_date: dateStr, end_date: dateStr }
          }).catch(() => ({ data: [] })),

          // Fetch pending daily to accommodation (evening route)
          axios.get(`${API_BASE_URL}/api/local-transports/pending_daily_to_accommodation/`, {
            headers,
            params: { project: selectedProject, start_date: dateStr, end_date: dateStr }
          }).catch(() => ({ data: [] })),

          // Fetch ResourcePlanningAssignments to get users assigned to this project on this date
          axios.get(`${API_BASE_URL}/api/resource-planning-assignments/`, {
            headers,
            params: { project: selectedProject }
          }).catch(() => ({ data: [] })),

          // Fetch VehicleAssignments to get cars assigned to this project
          axios.get(`${API_BASE_URL}/api/vehicle-assignments/`, {
            headers,
            params: { project: selectedProject }
          }).catch(() => ({ data: [] })),

          // Fetch Accommodations for this project
          axios.get(`${API_BASE_URL}/api/accommodation/`, {
            headers,
            params: { project: selectedProject }
          }).catch(() => ({ data: [] })),

          // Fetch Room Assignments to know where users are staying
          axios.get(`${API_BASE_URL}/api/room-assignments/`, {
            headers
          }).catch(() => ({ data: [] })),

          // Fetch recent transports (last 7 days) for "Copy from recent day" feature
          axios.get(`${API_BASE_URL}/api/local-transports/`, {
            headers,
            params: {
              project: selectedProject,
              start_date: format(subDays(selectedDate, 7), 'yyyy-MM-dd'),
              end_date: format(subDays(selectedDate, 1), 'yyyy-MM-dd')
            }
          }).catch(() => ({ data: [] }))
        ]);

        setLocalTransports(transportsRes.data);
        setRecentTransports(yesterdayTransportsRes.data);
        setPendingPickups(pickupsRes.data);
        setPendingDropoffs(dropoffsRes.data);
        setPendingToProject(toProjectRes.data);
        setPendingToAccommodation(toAccommodationRes.data);
        setAccommodations(accommodationsRes.data || []);

        // Process room assignments - map user_id to their accommodation for selected date
        const roomAssignments = roomAssignmentsRes.data || [];
        const accList = accommodationsRes.data || [];
        const userAccMap = {};
        roomAssignments.forEach(ra => {
          const startDate = new Date(ra.start_date);
          const endDate = new Date(ra.end_date);
          const currentDate = new Date(dateStr);

          // Check if assignment is active on selected date
          if (currentDate >= startDate && currentDate <= endDate) {
            const accId = ra.room_data?.accommodation_id || ra.accommodation_id;
            // Find full accommodation data
            const fullAccommodation = accList.find(a => a.id === accId);

            userAccMap[ra.user] = {
              accommodation_id: accId,
              accommodation_name: ra.room_data?.accommodation_name || ra.accommodation_name || 'Unknown',
              room_name: ra.room_data?.room_number || ra.room_number || '',
              // Add full address data from accommodation
              address: fullAccommodation?.address || '',
              city: fullAccommodation?.city || '',
              post_code: fullAccommodation?.post_code || '',
              country: fullAccommodation?.country || '',
              phone: fullAccommodation?.phone || ''
            };
          }
        });
        setUserAccommodations(userAccMap);

        // Filter users: those with ResourcePlanningAssignment for this project where selected date is within their assignment period
        const userAssignments = userAssignmentsRes.data || [];
        const assignedUserIds = new Set();
        const currentDate = new Date(dateStr);

        userAssignments.forEach(assignment => {
          // Exclude special tiles (Day Off, Holiday, etc.)
          if (!assignment.is_special_tile) {
            const startDate = new Date(assignment.start_date);
            const endDate = new Date(assignment.end_date);
            // User is on project if selected date is within their assignment period
            if (currentDate >= startDate && currentDate <= endDate) {
              assignedUserIds.add(assignment.user);
            }
          }
        });

        const filteredUsers = allUsers.filter(user => assignedUserIds.has(user.id));
        setUsers(filteredUsers);

        // Filter cars: those with VehicleAssignment for this project where selected date is within assignment period
        const vehicleAssignments = carAssignmentsRes.data || [];
        const assignedCarIds = new Set();

        vehicleAssignments.forEach(assignment => {
          // VehicleAssignment uses assigned_date and expected_return_date
          const assignedDate = new Date(assignment.assigned_date);
          const returnDate = assignment.expected_return_date ? new Date(assignment.expected_return_date) : null;

          // Car is available if selected date is between assigned_date and expected_return_date
          // Use date-only comparison (ignore time)
          const assignedDateOnly = new Date(assignedDate.getFullYear(), assignedDate.getMonth(), assignedDate.getDate());
          const returnDateOnly = returnDate ? new Date(returnDate.getFullYear(), returnDate.getMonth(), returnDate.getDate()) : null;
          const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());

          if (currentDateOnly >= assignedDateOnly) {
            if (!returnDateOnly || currentDateOnly <= returnDateOnly) {
              assignedCarIds.add(assignment.car);
            }
          }
        });

        const filteredCars = allCars.filter(car => assignedCarIds.has(car.id));
        setCars(filteredCars);

      } catch (error) {
        console.error('Error fetching transport data:', error);
      }
    };

    fetchTransportData();
  }, [selectedDate, selectedProject, allUsers, allCars, refreshTrigger]);

  // Handle person dropped on car
  const handlePersonDropOnCar = (person, car) => {
    const tabKey = airportSubTab; // 'pickups' or 'dropoffs'
    console.log('Dropping person on car:', person, car, 'tab:', tabKey);

    setCarAssignments(prev => {
      const tabAssignments = prev[tabKey] || {};
      const currentPassengers = tabAssignments[car.id] || [];
      // Get person's unique identifier
      const personId = person.user_id || person.id;

      // Don't add if already assigned
      const alreadyAssigned = currentPassengers.some(p => {
        const pId = p.user_id || p.id;
        return pId === personId;
      });

      if (alreadyAssigned) {
        console.log('Person already assigned to this car');
        return prev;
      }

      // Check if car has space
      const maxSeats = car.number_of_seats || 5;
      if (currentPassengers.length >= maxSeats) {
        console.log('Car is full');
        return prev;
      }

      // Calculate new passengers list
      const newPassengers = [...currentPassengers, person];

      // Auto-set pickup time based on transport type
      if (mainTab === MAIN_TABS.AIRPORT_TRANSFERS) {
        if (airportSubTab === AIRPORT_SUB_TABS.PICKUPS) {
          // For pickups: set time to latest arrival
          const arrivalTimes = newPassengers
            .map(p => p.arrival_time)
            .filter(Boolean)
            .map(t => t.slice(0, 5)); // Get HH:mm format

          if (arrivalTimes.length > 0) {
            const latestArrival = arrivalTimes.reduce((latest, t) => t > latest ? t : latest, '00:00');
            setCarPickupTimes(prevTimes => ({
              ...prevTimes,
              [tabKey]: {
                ...(prevTimes[tabKey] || {}),
                [car.id]: latestArrival
              }
            }));
          }
        } else if (airportSubTab === AIRPORT_SUB_TABS.DROPOFFS) {
          // For dropoffs: set preliminary time (will be updated after route optimization)
          // Uses 2h30min before earliest departure as initial estimate (no route duration yet)
          const departureTimes = newPassengers
            .map(p => p.departure_time)
            .filter(Boolean)
            .map(t => t.slice(0, 5));

          if (departureTimes.length > 0) {
            const earliestDeparture = departureTimes.reduce((earliest, t) => t < earliest ? t : earliest, '23:59');
            // Use calculateRecommendedDropoffTime with 0 route duration as preliminary estimate
            // Route optimization will update this with actual travel time
            const recommendedTime = calculateRecommendedDropoffTime(earliestDeparture, 0);
            if (recommendedTime) {
              setCarPickupTimes(prevTimes => ({
                ...prevTimes,
                [tabKey]: {
                  ...(prevTimes[tabKey] || {}),
                  [car.id]: recommendedTime
                }
              }));
            }
          }
        }
      }

      // Auto-select this car for map display
      setActiveCarId(car.id);
      setViewingTransportId(null);

      console.log('Adding person to car, current passengers:', currentPassengers.length);
      return {
        ...prev,
        [tabKey]: {
          ...tabAssignments,
          [car.id]: newPassengers
        }
      };
    });
  };

  // Remove person from car
  const handleRemovePersonFromCar = (carId, person) => {
    const tabKey = airportSubTab; // 'pickups' or 'dropoffs'
    setCarAssignments(prev => {
      const tabAssignments = prev[tabKey] || {};
      const currentPassengers = tabAssignments[carId] || [];
      return {
        ...prev,
        [tabKey]: {
          ...tabAssignments,
          [carId]: currentPassengers.filter(p => (p.id || p.user_id) !== (person.id || person.user_id))
        }
      };
    });
  };

  // Handle person dropped on car for Daily Planning
  const handleDailyPersonDropOnCar = (person, car) => {
    const tabKey = getDailyTabKey(); // 'to_project' or 'to_accommodation'
    console.log('Dropping daily person on car:', person, car, 'tab:', tabKey);

    setDailyCarAssignments(prev => {
      const tabAssignments = prev[tabKey] || {};
      const currentPassengers = tabAssignments[car.id] || [];
      const personId = person.user_id || person.id;

      // Don't add if already assigned
      const alreadyAssigned = currentPassengers.some(p => {
        const pId = p.user_id || p.id;
        return pId === personId;
      });

      if (alreadyAssigned) {
        console.log('Person already assigned to this car');
        return prev;
      }

      // Check if car has space
      const maxSeats = car.number_of_seats || 5;
      if (currentPassengers.length >= maxSeats) {
        console.log('Car is full');
        return prev;
      }

      const newPassengers = [...currentPassengers, person];

      // Auto-set pickup time based on daily tab
      if (tabKey === 'to_project') {
        // Morning route - default 07:00
        setDailyCarPickupTimes(prevTimes => ({
          ...prevTimes,
          [tabKey]: {
            ...(prevTimes[tabKey] || {}),
            [car.id]: prevTimes[tabKey]?.[car.id] || '07:00'
          }
        }));
      } else {
        // Evening route - default 16:00
        setDailyCarPickupTimes(prevTimes => ({
          ...prevTimes,
          [tabKey]: {
            ...(prevTimes[tabKey] || {}),
            [car.id]: prevTimes[tabKey]?.[car.id] || '16:00'
          }
        }));
      }

      // Auto-select this car for map display
      setActiveCarId(car.id);
      setViewingTransportId(null);

      console.log('Adding daily person to car, current passengers:', currentPassengers.length);
      return {
        ...prev,
        [tabKey]: {
          ...tabAssignments,
          [car.id]: newPassengers
        }
      };
    });
  };

  // Remove person from car for Daily Planning
  const handleRemoveDailyPersonFromCar = (carId, person) => {
    const tabKey = getDailyTabKey();
    setDailyCarAssignments(prev => {
      const tabAssignments = prev[tabKey] || {};
      const currentPassengers = tabAssignments[carId] || [];
      return {
        ...prev,
        [tabKey]: {
          ...tabAssignments,
          [carId]: currentPassengers.filter(p => (p.id || p.user_id) !== (person.id || person.user_id))
        }
      };
    });
  };

  // Helper: Get day name for display (yesterday, Friday, Thursday, etc.)
  const getDayLabel = (transportDate) => {
    const today = new Date(format(selectedDate, 'yyyy-MM-dd'));
    const tDate = new Date(transportDate);
    const diffDays = Math.round((today - tDate) / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'yesterday';
    if (diffDays <= 7) {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return dayNames[tDate.getDay()];
    }
    return format(tDate, 'MMM d');
  };

  // Helper: Find most recent transport for a car (searches backwards up to 7 days)
  const findRecentTransport = (carId, transportType) => {
    // Filter transports for this car and type
    const carTransports = recentTransports.filter(
      t => t.car === carId && t.transport_type === transportType
    );

    if (carTransports.length === 0) return null;

    // Sort by date descending (most recent first)
    const sorted = carTransports.sort((a, b) =>
      new Date(b.transport_date) - new Date(a.transport_date)
    );

    return sorted[0];
  };

  // Copy from recent day's transport assignment to today
  const copyFromRecentDay = (carId, transportType = 'daily_to_work') => {
    // Find most recent transport for this car
    const recentTransport = findRecentTransport(carId, transportType);

    if (!recentTransport) {
      alert('No recent transport found for this car in the last 7 days.');
      return;
    }

    const dayLabel = getDayLabel(recentTransport.transport_date);

    // Check if car is available today
    const carAvailable = cars.some(c => c.id === carId);
    if (!carAvailable) {
      alert('This car is not available today.');
      return;
    }

    // Get passengers from recent transport
    const recentPassengerIds = recentTransport.passengers || [];
    if (recentPassengerIds.length === 0) {
      alert(`Transport from ${dayLabel} had no passengers.`);
      return;
    }

    // Get pending people for today based on transport type
    const pendingPeople = transportType === 'daily_to_work' ? pendingToProject : pendingToAccommodation;
    const tabKey = transportType === 'daily_to_work' ? 'to_project' : 'to_accommodation';

    // Get currently assigned people for this tab
    const currentTabAssignments = dailyCarAssignments[tabKey] || {};
    const allAssignedIds = Object.values(currentTabAssignments)
      .flat()
      .map(p => p.id || p.user_id);

    // Filter: people who were in recent transport AND are available today AND not already assigned
    const availablePassengers = pendingPeople.filter(person => {
      const personId = person.user_id || person.id;
      const wasInRecent = recentPassengerIds.includes(personId);
      const notAssigned = !allAssignedIds.includes(personId);
      return wasInRecent && notAssigned;
    });

    if (availablePassengers.length === 0) {
      alert(`No passengers from ${dayLabel}'s route are available today (they may already be assigned or not on the project today).`);
      return;
    }

    // Get current passengers in this car (if any)
    const currentPassengers = currentTabAssignments[carId] || [];

    // Add available passengers to the car
    setDailyCarAssignments(prev => ({
      ...prev,
      [tabKey]: {
        ...(prev[tabKey] || {}),
        [carId]: [...currentPassengers, ...availablePassengers]
      }
    }));

    // Copy time from recent transport if available
    if (recentTransport.scheduled_time) {
      const time = recentTransport.scheduled_time.slice(0, 5); // HH:mm
      setDailyCarPickupTimes(prev => ({
        ...prev,
        [tabKey]: {
          ...(prev[tabKey] || {}),
          [carId]: time
        }
      }));
    }

    // Auto-select this car for map display
    setActiveCarId(carId);
    setViewingTransportId(null);

    // Show info about what was copied
    const notAvailableCount = recentPassengerIds.length - availablePassengers.length;
    if (notAvailableCount > 0) {
      alert(`Copied ${availablePassengers.length} passenger(s) from ${dayLabel}. ${notAvailableCount} passenger(s) are not available today.`);
    }
  };

  // Check if a car has recent transport available for copying (legacy compatibility)
  const hasYesterdayTransport = (carId, transportType = 'daily_to_work') => {
    return recentTransports.some(t => t.car === carId && t.transport_type === transportType);
  };

  // Get detailed status for "Copy from recent day" button
  const getCopyFromRecentDayStatus = (carId, transportType = 'daily_to_work') => {
    // Find most recent transport for this car
    const recentTransport = findRecentTransport(carId, transportType);

    if (!recentTransport) {
      return { canCopy: false, reason: 'No transport in the last 7 days', dayLabel: null };
    }

    const dayLabel = getDayLabel(recentTransport.transport_date);

    // Check if recent transport had passengers
    const recentPassengerIds = recentTransport.passengers || [];
    if (recentPassengerIds.length === 0) {
      return { canCopy: false, reason: `Transport from ${dayLabel} had no passengers`, dayLabel };
    }

    // Get pending people for today
    const pendingPeople = transportType === 'daily_to_work' ? pendingToProject : pendingToAccommodation;
    const tabKey = transportType === 'daily_to_work' ? 'to_project' : 'to_accommodation';

    // Get currently assigned people for this tab
    const currentTabAssignments = dailyCarAssignments[tabKey] || {};
    const allAssignedIds = Object.values(currentTabAssignments)
      .flat()
      .map(p => p.id || p.user_id);

    // Count available passengers from recent transport
    const availablePassengers = pendingPeople.filter(person => {
      const personId = person.user_id || person.id;
      const wasInRecent = recentPassengerIds.includes(personId);
      const notAssigned = !allAssignedIds.includes(personId);
      return wasInRecent && notAssigned;
    });

    if (availablePassengers.length === 0) {
      return { canCopy: false, reason: `No passengers from ${dayLabel} are available today`, dayLabel };
    }

    return {
      canCopy: true,
      reason: `Copy ${availablePassengers.length} passenger(s) from ${dayLabel}`,
      dayLabel,
      availableCount: availablePassengers.length,
      totalRecent: recentPassengerIds.length
    };
  };

  // Optimize route for a car - finds best order of stops and gets road geometry
  const optimizeCarRoute = async (carId) => {
    const tabKey = airportSubTab;
    const tabAssignments = carAssignments[tabKey] || {};
    const passengers = tabAssignments[carId] || [];

    if (passengers.length === 0) {
      // Clear optimization data if no passengers
      setOptimizedRoutes(prev => ({
        ...prev,
        [tabKey]: { ...prev[tabKey], [carId]: null }
      }));
      return;
    }

    // Get unique accommodations for passengers
    const passengersByAccommodation = {};
    passengers.forEach(person => {
      const personId = person.user_id || person.id;
      const userAcc = userAccommodations[personId];
      if (userAcc?.accommodation_id) {
        const accId = userAcc.accommodation_id;
        const accName = userAcc.accommodation_name || 'Unknown';
        if (!passengersByAccommodation[accName]) {
          passengersByAccommodation[accName] = {
            accommodation_id: accId,
            passengers: []
          };
        }
        passengersByAccommodation[accName].passengers.push(person);
      }
    });

    const accommodationEntries = Object.entries(passengersByAccommodation);
    if (accommodationEntries.length === 0) return;

    setRouteOptimizing(prev => ({ ...prev, [carId]: true }));

    try {
      const isPickup = airportSubTab === AIRPORT_SUB_TABS.PICKUPS;

      // Get airport coordinates
      const airportCode = isPickup
        ? passengers[0]?.arrival_airport
        : passengers[0]?.departure_airport;
      const airportCoords = getAirportCoordinates(airportCode);

      if (!airportCoords) {
        console.warn('No airport coordinates found for:', airportCode);
        setRouteOptimizing(prev => ({ ...prev, [carId]: false }));
        return;
      }

      // Get driver accommodation (first passenger or designated driver)
      const driverId = passengers[0]?.user_id || passengers[0]?.id;
      const driverAcc = userAccommodations[driverId];
      const driverAccDetails = driverAcc ? accommodations.find(a => a.id === driverAcc.accommodation_id) : null;

      // Build waypoints from passenger accommodations (exclude driver's accommodation as it's the end point)
      const waypoints = [];
      accommodationEntries.forEach(([accName, accData]) => {
        const acc = accommodations.find(a => a.id === accData.accommodation_id);
        if (acc && acc.latitude && acc.longitude) {
          // Skip driver's accommodation if it's the end point
          if (driverAccDetails && acc.id === driverAccDetails.id) return;

          waypoints.push({
            lat: parseFloat(acc.latitude),
            lon: parseFloat(acc.longitude),
            name: accName,
            accommodation_id: acc.id,
            passengers: accData.passengers
          });
        }
      });

      // Determine start and end points based on transport type
      let start, end;
      if (isPickup) {
        // Pickup: Airport -> Accommodations -> Driver's home
        start = {
          lat: airportCoords.lat,
          lon: airportCoords.lng,
          name: `${airportCode} Airport`
        };
        end = driverAccDetails ? {
          lat: parseFloat(driverAccDetails.latitude),
          lon: parseFloat(driverAccDetails.longitude),
          name: driverAccDetails.name
        } : {
          lat: airportCoords.lat,
          lon: airportCoords.lng,
          name: `${airportCode} Airport`
        };
      } else {
        // Dropoff: Driver's home -> Accommodations -> Airport
        start = driverAccDetails ? {
          lat: parseFloat(driverAccDetails.latitude),
          lon: parseFloat(driverAccDetails.longitude),
          name: driverAccDetails.name
        } : {
          lat: airportCoords.lat,
          lon: airportCoords.lng,
          name: `${airportCode} Airport`
        };
        end = {
          lat: airportCoords.lat,
          lon: airportCoords.lng,
          name: `${airportCode} Airport`
        };
      }

      // Call optimization API (also handles simple routes with 0-1 waypoints to get road geometry)
      const headers = getHeaders();
      const response = await axios.post(`${API_BASE_URL}/api/routing/optimize/`, {
        start,
        end,
        waypoints
      }, { headers });

      // Store optimization result
      setOptimizedRoutes(prev => ({
        ...prev,
        [tabKey]: {
          ...prev[tabKey],
          [carId]: {
            ...response.data,
            waypoints, // Keep original waypoints for reference
            airportCode
          }
        }
      }));

      // For dropoffs: calculate recommended departure time based on route duration
      // Car must arrive at airport 2h before earliest flight
      if (!isPickup && response.data.total_duration_minutes) {
        // Find earliest departure time among passengers
        const departureTimes = passengers
          .map(p => p.departure_time)
          .filter(Boolean)
          .map(t => t.slice(0, 5));

        if (departureTimes.length > 0) {
          const earliestDeparture = departureTimes.reduce((earliest, t) => t < earliest ? t : earliest, '23:59');
          const recommendedTime = calculateRecommendedDropoffTime(earliestDeparture, response.data.total_duration_minutes);

          if (recommendedTime) {
            setCarPickupTimes(prev => ({
              ...prev,
              [tabKey]: {
                ...(prev[tabKey] || {}),
                [carId]: recommendedTime
              }
            }));
          }
        }
      }

      // Auto-calculate end time based on route duration
      const tabPickupTimes = carPickupTimes[tabKey] || {};
      let startTime = tabPickupTimes[carId];

      // For dropoffs, use the newly calculated recommended time if available
      if (!isPickup && response.data.total_duration_minutes) {
        const departureTimes = passengers
          .map(p => p.departure_time)
          .filter(Boolean)
          .map(t => t.slice(0, 5));
        if (departureTimes.length > 0) {
          const earliestDeparture = departureTimes.reduce((earliest, t) => t < earliest ? t : earliest, '23:59');
          startTime = calculateRecommendedDropoffTime(earliestDeparture, response.data.total_duration_minutes) || startTime;
        }
      }

      // Auto-calculate end time only if not manually set by admin
      const isManualEndTime = manualEndTimes[tabKey]?.[carId];
      if (startTime && response.data.total_duration_minutes && !isManualEndTime) {
        const [hours, minutes] = startTime.split(':').map(Number);
        const totalMinutes = hours * 60 + minutes + response.data.total_duration_minutes;
        const endHours = Math.floor(totalMinutes / 60) % 24;
        const endMins = totalMinutes % 60;
        const endTime = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;

        setCarEndTimes(prev => ({
          ...prev,
          [tabKey]: { ...prev[tabKey], [carId]: endTime }
        }));
      }

    } catch (error) {
      console.error('Route optimization failed:', error);
    } finally {
      setRouteOptimizing(prev => ({ ...prev, [carId]: false }));
    }
  };

  // Trigger route optimization when car assignments change (with debouncing)
  useEffect(() => {
    const tabKey = airportSubTab;
    const tabAssignments = carAssignments[tabKey] || {};
    const DEBOUNCE_MS = 1500; // Wait 1.5s after last change before optimizing

    // Process each car with assignments
    Object.keys(tabAssignments).forEach(carId => {
      const passengers = tabAssignments[carId] || [];
      const timerKey = `${tabKey}_${carId}`;

      // Clear any existing debounce timer for this car
      if (optimizeDebounceTimers.current[timerKey]) {
        clearTimeout(optimizeDebounceTimers.current[timerKey]);
      }

      if (passengers.length === 0) {
        // Clear optimization data and previous accommodations if no passengers
        prevAccommodationSets.current[timerKey] = new Set();
        return;
      }

      // Get current set of accommodation IDs for this car's passengers
      const currentAccommodationIds = new Set();
      passengers.forEach(person => {
        const personId = person.user_id || person.id;
        const userAcc = userAccommodations[personId];
        if (userAcc?.accommodation_id) {
          currentAccommodationIds.add(userAcc.accommodation_id);
        }
      });

      // Check if accommodations have changed (not just more people at same places)
      const prevSet = prevAccommodationSets.current[timerKey] || new Set();
      const accommodationsChanged =
        currentAccommodationIds.size !== prevSet.size ||
        [...currentAccommodationIds].some(id => !prevSet.has(id));

      if (!accommodationsChanged && prevSet.size > 0) {
        // Same accommodations, no need to re-optimize
        return;
      }

      // Debounce the optimization call
      optimizeDebounceTimers.current[timerKey] = setTimeout(() => {
        // Update previous accommodations set
        prevAccommodationSets.current[timerKey] = currentAccommodationIds;
        optimizeCarRoute(parseInt(carId));
      }, DEBOUNCE_MS);
    });

    // Cleanup function
    return () => {
      Object.keys(optimizeDebounceTimers.current).forEach(key => {
        if (key.startsWith(tabKey + '_')) {
          clearTimeout(optimizeDebounceTimers.current[key]);
        }
      });
    };
  }, [carAssignments, airportSubTab, userAccommodations, accommodations]);

  // Optimize route for a car in Daily Planning mode
  const optimizeDailyCarRoute = async (carId) => {
    const tabKey = getDailyTabKey();
    const tabAssignments = dailyCarAssignments[tabKey] || {};
    const passengers = tabAssignments[carId] || [];

    if (passengers.length === 0) {
      // Clear optimization data if no passengers
      setDailyOptimizedRoutes(prev => ({
        ...prev,
        [tabKey]: { ...prev[tabKey], [carId]: null }
      }));
      return;
    }

    // Get current project for coordinates
    const currentProject = projects.find(p => p.id === parseInt(selectedProject));
    if (!currentProject?.latitude || !currentProject?.longitude) {
      console.warn('Project has no coordinates');
      return;
    }

    const projectCoords = {
      lat: parseFloat(currentProject.latitude),
      lng: parseFloat(currentProject.longitude)
    };

    // Get unique accommodations for passengers
    const passengersByAccommodation = {};
    passengers.forEach(person => {
      const personId = person.user_id || person.id;
      const accId = person.accommodation_id;
      const accName = person.accommodation_name || 'Unknown';
      if (accId) {
        if (!passengersByAccommodation[accName]) {
          passengersByAccommodation[accName] = {
            accommodation_id: accId,
            passengers: []
          };
        }
        passengersByAccommodation[accName].passengers.push(person);
      }
    });

    const accommodationEntries = Object.entries(passengersByAccommodation);
    if (accommodationEntries.length === 0) return;

    setRouteOptimizing(prev => ({ ...prev, [carId]: true }));

    try {
      const isToProject = dailySubTab === DAILY_SUB_TABS.TO_PROJECT;

      // Build waypoints from accommodations
      const waypoints = [];
      for (const [accName, accData] of accommodationEntries) {
        const acc = accommodations.find(a => a.id === accData.accommodation_id);
        if (acc && acc.latitude && acc.longitude) {
          waypoints.push({
            lat: parseFloat(acc.latitude),
            lon: parseFloat(acc.longitude),
            name: accName,
            accommodation_id: acc.id,
            passengers: accData.passengers
          });
        } else {
          // Try to get coords from passenger data
          const firstPassenger = accData.passengers[0];
          if (firstPassenger?.latitude && firstPassenger?.longitude) {
            waypoints.push({
              lat: parseFloat(firstPassenger.latitude),
              lon: parseFloat(firstPassenger.longitude),
              name: accName,
              passengers: accData.passengers
            });
          }
        }
      }

      if (waypoints.length === 0) {
        setRouteOptimizing(prev => ({ ...prev, [carId]: false }));
        return;
      }

      // For To Project: start from first accommodation, visit other accommodations, end at project
      // For To Accommodation: start from project, visit other accommodations, end at last accommodation
      let start, end;
      let intermediateWaypoints = [];

      if (isToProject) {
        // Start from first accommodation, end at project
        start = { lat: waypoints[0].lat, lon: waypoints[0].lon, name: waypoints[0].name };
        end = { lat: projectCoords.lat, lon: projectCoords.lng, name: currentProject.name };
        // Intermediate waypoints are all except the first one
        intermediateWaypoints = waypoints.slice(1);
      } else {
        // Start from project, end at last accommodation
        start = { lat: projectCoords.lat, lon: projectCoords.lng, name: currentProject.name };
        end = { lat: waypoints[waypoints.length - 1].lat, lon: waypoints[waypoints.length - 1].lon, name: waypoints[waypoints.length - 1].name };
        // Intermediate waypoints are all except the last one
        intermediateWaypoints = waypoints.slice(0, -1);
      }

      // Call optimization API (also handles simple routes with 0 intermediate waypoints to get road geometry)
      const headers = getHeaders();
      const response = await axios.post(`${API_BASE_URL}/api/routing/optimize/`, {
        start,
        end,
        waypoints: intermediateWaypoints
      }, { headers });

      // Store optimization result
      setDailyOptimizedRoutes(prev => ({
        ...prev,
        [tabKey]: {
          ...prev[tabKey],
          [carId]: {
            ...response.data,
            waypoints: waypoints, // Keep all waypoints for reference (including start/end accommodations)
            projectName: currentProject.name
          }
        }
      }));

      // Auto-calculate end time based on route duration
      const tabPickupTimes = dailyCarPickupTimes[tabKey] || {};
      const startTime = tabPickupTimes[carId] || (isToProject ? '07:00' : '16:00');
      if (startTime && response.data.total_duration_minutes) {
        const [hours, minutes] = startTime.split(':').map(Number);
        const totalMinutes = hours * 60 + minutes + response.data.total_duration_minutes;
        const endHours = Math.floor(totalMinutes / 60) % 24;
        const endMins = totalMinutes % 60;
        const endTime = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;

        setDailyCarEndTimes(prev => ({
          ...prev,
          [tabKey]: { ...prev[tabKey], [carId]: endTime }
        }));
      }

    } catch (error) {
      console.error('Daily route optimization failed:', error);
    } finally {
      setRouteOptimizing(prev => ({ ...prev, [carId]: false }));
    }
  };

  // Trigger route optimization for Daily Planning when car assignments change (with debouncing)
  useEffect(() => {
    if (mainTab !== MAIN_TABS.DAILY_PLANNING) return;

    const tabKey = getDailyTabKey();
    const tabAssignments = dailyCarAssignments[tabKey] || {};
    const DEBOUNCE_MS = 1500; // Wait 1.5s after last change before optimizing

    // Process each car with assignments
    Object.keys(tabAssignments).forEach(carId => {
      const passengers = tabAssignments[carId] || [];
      const timerKey = `daily_${tabKey}_${carId}`;

      // Clear any existing debounce timer for this car
      if (optimizeDebounceTimers.current[timerKey]) {
        clearTimeout(optimizeDebounceTimers.current[timerKey]);
      }

      if (passengers.length === 0) {
        // Clear previous accommodations if no passengers
        prevAccommodationSets.current[timerKey] = new Set();
        return;
      }

      // Get current set of accommodation IDs for this car's passengers
      const currentAccommodationIds = new Set();
      passengers.forEach(person => {
        const accId = person.accommodation_id;
        if (accId) {
          currentAccommodationIds.add(accId);
        }
      });

      // Check if accommodations have changed (not just more people at same places)
      const prevSet = prevAccommodationSets.current[timerKey] || new Set();
      const accommodationsChanged =
        currentAccommodationIds.size !== prevSet.size ||
        [...currentAccommodationIds].some(id => !prevSet.has(id));

      if (!accommodationsChanged && prevSet.size > 0) {
        // Same accommodations, no need to re-optimize
        return;
      }

      // Debounce the optimization call
      optimizeDebounceTimers.current[timerKey] = setTimeout(() => {
        // Update previous accommodations set
        prevAccommodationSets.current[timerKey] = currentAccommodationIds;
        optimizeDailyCarRoute(parseInt(carId));
      }, DEBOUNCE_MS);
    });

    // Cleanup function
    return () => {
      Object.keys(optimizeDebounceTimers.current).forEach(key => {
        if (key.startsWith(`daily_${tabKey}_`)) {
          clearTimeout(optimizeDebounceTimers.current[key]);
        }
      });
    };
  }, [dailyCarAssignments, dailySubTab, accommodations, selectedProject, mainTab]);

  // Create transport - creates ONE LocalTransport record with waypoints
  const createMultiStopRoute = async (car, passengersByAccommodation) => {
    try {
      const headers = getHeaders();

      // Determine transport type based on current tabs
      let transportType;
      if (mainTab === MAIN_TABS.AIRPORT_TRANSFERS) {
        transportType = airportSubTab === AIRPORT_SUB_TABS.PICKUPS ? 'airport_pickup' : 'airport_dropoff';
      } else {
        transportType = dailySubTab === DAILY_SUB_TABS.TO_PROJECT ? 'daily_to_work' : 'daily_to_home';
      }

      const project = projects.find(p => p.id === parseInt(selectedProject));
      const tabPickupTimes = carPickupTimes[airportSubTab] || {};
      const tabEndTimes = carEndTimes[airportSubTab] || {};
      const scheduledTime = tabPickupTimes[car.id] || '08:00';
      const scheduledEndTime = tabEndTimes[car.id] || null;
      const transportDate = format(selectedDate, 'yyyy-MM-dd');

      // Get all passengers
      const allPassengers = Object.values(passengersByAccommodation).flatMap(acc => acc.passengers);
      const driverId = allPassengers[0]?.user_id || allPassengers[0]?.id;

      // Convert passengersByAccommodation to sorted array
      const accommodationStops = Object.entries(passengersByAccommodation)
        .map(([accName, accData]) => ({
          ...accData,
          accommodation_name: accName,
          accommodationDetails: accommodations.find(a => a.id === accData.accommodation_id)
        }))
        .sort((a, b) => a.accommodation_name.localeCompare(b.accommodation_name));

      // Build waypoints array based on transport type
      const waypoints = [];
      let sequence = 1;

      if (transportType === 'airport_pickup') {
        // Airport Pickup: Airport → Acc1 → Acc2 → ...
        const airportCode = allPassengers[0]?.arrival_airport || 'AIRPORT';
        const airportCoords = getAirportCoordinates(airportCode);

        // Start: Airport
        waypoints.push({
          sequence: sequence++,
          type: 'airport',
          name: `${airportCode} Airport`,
          lat: roundCoord(airportCoords?.lat),
          lon: roundCoord(airportCoords?.lng),
          passenger_ids: allPassengers.map(p => p.user_id || p.id),
          action: 'pickup'
        });

        // Stops: Accommodations (dropoff points)
        accommodationStops.forEach(stop => {
          const acc = stop.accommodationDetails;
          waypoints.push({
            sequence: sequence++,
            type: 'accommodation',
            name: stop.accommodation_name,
            accommodation_id: stop.accommodation_id,
            lat: roundCoord(acc?.latitude),
            lon: roundCoord(acc?.longitude),
            address: acc ? `${acc.address || ''}, ${acc.city || ''}` : '',
            passenger_ids: stop.passengers.map(p => p.user_id || p.id),
            action: 'dropoff'
          });
        });

      } else if (transportType === 'airport_dropoff') {
        // Airport Dropoff: Acc1 → Acc2 → ... → Airport
        const airportCode = allPassengers[0]?.departure_airport || 'AIRPORT';
        const airportCoords = getAirportCoordinates(airportCode);

        // Stops: Accommodations (pickup points)
        accommodationStops.forEach(stop => {
          const acc = stop.accommodationDetails;
          waypoints.push({
            sequence: sequence++,
            type: 'accommodation',
            name: stop.accommodation_name,
            accommodation_id: stop.accommodation_id,
            lat: roundCoord(acc?.latitude),
            lon: roundCoord(acc?.longitude),
            address: acc ? `${acc.address || ''}, ${acc.city || ''}` : '',
            passenger_ids: stop.passengers.map(p => p.user_id || p.id),
            action: 'pickup'
          });
        });

        // End: Airport
        waypoints.push({
          sequence: sequence++,
          type: 'airport',
          name: `${airportCode} Airport`,
          lat: roundCoord(airportCoords?.lat),
          lon: roundCoord(airportCoords?.lng),
          passenger_ids: [],
          action: 'dropoff'
        });

      } else {
        // Daily routes handled by createDailyRoute function
        console.warn('Daily routes should use createDailyRoute');
        return;
      }

      // Build route description for notes
      const routeDescription = waypoints.map(wp => wp.name).join(' → ');

      // Get optimized route data if available (from optimizeCarRoute)
      const tabKey = transportType === 'airport_pickup' ? 'pickups' : 'dropoffs';
      const optimizedRoute = optimizedRoutes[tabKey]?.[car.id];

      // Calculate end time based on departure + duration if not manually set
      let calculatedEndTime = scheduledEndTime;
      if (!calculatedEndTime && optimizedRoute?.total_duration_minutes && scheduledTime) {
        const [hours, minutes] = scheduledTime.split(':').map(Number);
        const totalMinutes = hours * 60 + minutes + Math.round(optimizedRoute.total_duration_minutes);
        const endHours = Math.floor(totalMinutes / 60) % 24;
        const endMinutes = totalMinutes % 60;
        calculatedEndTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
      }

      // Create ONE transport record
      const payload = {
        project: selectedProject,
        transport_type: transportType,
        transport_date: transportDate,
        scheduled_time: scheduledTime,
        scheduled_end_time: calculatedEndTime,
        car: car.id,
        driver: driverId,
        passengers: allPassengers.map(p => p.user_id || p.id),
        waypoints: waypoints,
        route_geometry: optimizedRoute?.geometry || null,
        distance_km: optimizedRoute?.total_distance_km || null,
        duration_minutes: optimizedRoute?.total_duration_minutes || null,
        status: 'scheduled',
        notes: routeDescription,
        related_travel: allPassengers[0]?.travel_id || null
      };

      // Check if we're editing an existing transport for this car
      const existingTransport = editingTransportId
        ? localTransports.find(t => t.id === editingTransportId && t.car === car.id)
        : null;

      let response;
      if (existingTransport) {
        // Update existing transport
        response = await axios.patch(
          `${API_BASE_URL}/api/local-transports/${existingTransport.id}/`,
          payload,
          { headers }
        );
        // Update in state
        setLocalTransports(prev => prev.map(t =>
          t.id === existingTransport.id ? response.data : t
        ));
      } else {
        // Create new transport
        response = await axios.post(`${API_BASE_URL}/api/local-transports/`, payload, { headers });
        // Add to state
        setLocalTransports(prev => [...prev, response.data]);
      }

      // Remove all passengers from car assignment
      const allPassengerIds = allPassengers.map(p => p.user_id || p.id);
      setCarAssignments(prev => {
        const tabAssignments = prev[tabKey] || {};
        const { [car.id]: removed, ...restOfTab } = tabAssignments;
        return {
          ...prev,
          [tabKey]: restOfTab
        };
      });

      // Remove from pending lists (only for new transports, not edits)
      if (!existingTransport) {
        if (transportType === 'airport_pickup') {
          setPendingPickups(prev => prev.filter(p => !allPassengerIds.includes(p.user_id)));
        } else if (transportType === 'airport_dropoff') {
          setPendingDropoffs(prev => prev.filter(p => !allPassengerIds.includes(p.user_id)));
        }
      }

      // Clear editing state
      setEditingTransportId(null);
      // Trigger refresh to reload pending people lists
      setRefreshTrigger(prev => prev + 1);

    } catch (error) {
      console.error('Error saving transport:', error);
      alert('Failed to save route: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Delete transport
  const deleteTransport = async (transport) => {
    if (!window.confirm('Are you sure you want to delete this transport?')) return;

    try {
      const headers = getHeaders();
      await axios.delete(`${API_BASE_URL}/api/local-transports/${transport.id}/`, { headers });
      setLocalTransports(prev => prev.filter(t => t.id !== transport.id));
      // Trigger refresh to reload pending people lists
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting transport:', error);
      alert('Failed to delete transport');
    }
  };

  // Remove passenger from transport
  const removePassengerFromTransport = async (transportId, passengerId) => {
    try {
      const headers = getHeaders();
      const transport = localTransports.find(t => t.id === transportId);
      if (!transport) return;

      // Filter out the passenger
      const newPassengerIds = transport.passengers?.filter(id => id !== passengerId) ||
                              transport.passengers_data?.filter(p => p.id !== passengerId).map(p => p.id) || [];

      // Update waypoints to remove passenger from passenger_ids
      const updatedWaypoints = transport.waypoints?.map(wp => ({
        ...wp,
        passenger_ids: wp.passenger_ids?.filter(id => id !== passengerId) || []
      })) || [];

      const response = await axios.patch(
        `${API_BASE_URL}/api/local-transports/${transportId}/`,
        {
          passengers: newPassengerIds,
          waypoints: updatedWaypoints
        },
        { headers }
      );

      // Update state with response
      setLocalTransports(prev => prev.map(t =>
        t.id === transportId ? response.data : t
      ));
      // Trigger refresh to reload pending people lists
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error removing passenger:', error);
      alert('Failed to remove passenger: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Update transport (time, etc.)
  const updateTransport = async (transportId, updates) => {
    try {
      const headers = getHeaders();
      const response = await axios.patch(
        `${API_BASE_URL}/api/local-transports/${transportId}/`,
        updates,
        { headers }
      );

      // Update state with response
      setLocalTransports(prev => prev.map(t =>
        t.id === transportId ? response.data : t
      ));

      // Close edit mode
      setEditingTransportId(null);
      // Trigger refresh to reload pending people lists
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error updating transport:', error);
      alert('Failed to update transport: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Load a saved transport for editing in the planning panel
  const loadTransportForEditing = (transport) => {
    if (!transport) return;

    const carId = transport.car;
    const passengers = transport.passengers_data || [];

    // Determine which tab and assignments to use
    const isAirportTransport = transport.transport_type === 'airport_pickup' || transport.transport_type === 'airport_dropoff';
    const isDailyTransport = transport.transport_type === 'daily_to_work' || transport.transport_type === 'daily_to_home';

    if (isAirportTransport) {
      const tabKey = transport.transport_type === 'airport_pickup' ? 'pickups' : 'dropoffs';

      // Switch to Airport Transfers tab and correct sub-tab
      setMainTab(MAIN_TABS.AIRPORT_TRANSFERS);
      setAirportSubTab(tabKey === 'pickups' ? AIRPORT_SUB_TABS.PICKUPS : AIRPORT_SUB_TABS.DROPOFFS);

      // Load passengers into car assignments
      setCarAssignments(prev => ({
        ...prev,
        [tabKey]: {
          ...prev[tabKey],
          [carId]: passengers.map(p => ({
            ...p,
            user_id: p.id,
            // Include travel info if available from transport
            arrival_airport: transport.waypoints?.find(wp => wp.type === 'airport')?.name?.match(/([A-Z]{3})/)?.[1],
            departure_airport: transport.waypoints?.find(wp => wp.type === 'airport')?.name?.match(/([A-Z]{3})/)?.[1]
          }))
        }
      }));

      // Set time
      setCarPickupTimes(prev => ({
        ...prev,
        [tabKey]: {
          ...prev[tabKey],
          [carId]: transport.scheduled_time?.substring(0, 5) || '08:00'
        }
      }));

      if (transport.scheduled_end_time) {
        setCarEndTimes(prev => ({
          ...prev,
          [tabKey]: {
            ...prev[tabKey],
            [carId]: transport.scheduled_end_time.substring(0, 5)
          }
        }));
      }

      // Load existing optimized route if available
      if (transport.route_geometry) {
        setOptimizedRoutes(prev => ({
          ...prev,
          [tabKey]: {
            ...prev[tabKey],
            [carId]: {
              geometry: transport.route_geometry,
              total_distance_km: transport.distance_km,
              total_duration_minutes: transport.duration_minutes,
              optimized: true
            }
          }
        }));
      }

    } else if (isDailyTransport) {
      const tabKey = transport.transport_type === 'daily_to_work' ? 'to_project' : 'to_accommodation';

      // Switch to Daily Planning tab and correct sub-tab
      setMainTab(MAIN_TABS.DAILY_PLANNING);
      setDailySubTab(tabKey === 'to_project' ? DAILY_SUB_TABS.TO_PROJECT : DAILY_SUB_TABS.TO_ACCOMMODATION);

      // Load passengers into daily car assignments
      setDailyCarAssignments(prev => ({
        ...prev,
        [tabKey]: {
          ...prev[tabKey],
          [carId]: passengers.map(p => ({
            ...p,
            user_id: p.id,
            accommodation_id: transport.waypoints?.find(wp => wp.type === 'accommodation' && wp.passenger_ids?.includes(p.id))?.accommodation_id,
            accommodation_name: transport.waypoints?.find(wp => wp.type === 'accommodation' && wp.passenger_ids?.includes(p.id))?.name
          }))
        }
      }));

      // Set time
      setDailyCarPickupTimes(prev => ({
        ...prev,
        [tabKey]: {
          ...prev[tabKey],
          [carId]: transport.scheduled_time?.substring(0, 5) || (tabKey === 'to_project' ? '07:00' : '16:00')
        }
      }));

      if (transport.scheduled_end_time) {
        setDailyCarEndTimes(prev => ({
          ...prev,
          [tabKey]: {
            ...prev[tabKey],
            [carId]: transport.scheduled_end_time.substring(0, 5)
          }
        }));
      }

      // Load existing optimized route if available
      if (transport.route_geometry) {
        setDailyOptimizedRoutes(prev => ({
          ...prev,
          [tabKey]: {
            ...prev[tabKey],
            [carId]: {
              geometry: transport.route_geometry,
              total_distance_km: transport.distance_km,
              total_duration_minutes: transport.duration_minutes,
              optimized: true
            }
          }
        }));
      }
    }

    // Set editing state
    setEditingTransportId(transport.id);
    setActiveCarId(carId);
    setViewingTransportId(null);

    // Clear previous accommodation set tracking so optimization runs fresh if needed
    const timerKey = isAirportTransport
      ? `${transport.transport_type === 'airport_pickup' ? 'pickups' : 'dropoffs'}_${carId}`
      : `daily_${transport.transport_type === 'daily_to_work' ? 'to_project' : 'to_accommodation'}_${carId}`;
    prevAccommodationSets.current[timerKey] = new Set();
  };

  // Create daily route (To Project or To Accommodation)
  const createDailyRoute = async (car, passengers) => {
    if (passengers.length === 0) return;

    try {
      const headers = getHeaders();
      const tabKey = getDailyTabKey();
      const isToProject = dailySubTab === DAILY_SUB_TABS.TO_PROJECT;
      const transportType = isToProject ? 'daily_to_work' : 'daily_to_home';
      const project = projects.find(p => p.id === parseInt(selectedProject));
      const transportDate = format(selectedDate, 'yyyy-MM-dd');

      const tabPickupTimes = dailyCarPickupTimes[tabKey] || {};
      const tabEndTimes = dailyCarEndTimes[tabKey] || {};
      const scheduledTime = tabPickupTimes[car.id] || (isToProject ? '07:00' : '16:00');
      const scheduledEndTime = tabEndTimes[car.id] || null;

      // Group passengers by accommodation
      const passengersByAccommodation = {};
      passengers.forEach(p => {
        const accName = p.accommodation_name || 'No accommodation';
        const accId = p.accommodation_id;
        if (!passengersByAccommodation[accName]) {
          passengersByAccommodation[accName] = {
            accommodation_id: accId,
            passengers: []
          };
        }
        passengersByAccommodation[accName].passengers.push(p);
      });

      // Convert to array and sort
      const accommodationStops = Object.entries(passengersByAccommodation)
        .map(([accName, accData]) => ({
          ...accData,
          accommodation_name: accName,
          accommodationDetails: accommodations.find(a => a.id === accData.accommodation_id)
        }))
        .sort((a, b) => a.accommodation_name.localeCompare(b.accommodation_name));

      const allPassengers = passengers;
      const driverId = allPassengers[0]?.user_id || allPassengers[0]?.id;
      const totalStops = accommodationStops.length;

      // Get optimized route data if available
      const optimizedRoute = dailyOptimizedRoutes[tabKey]?.[car.id];

      // For multi-stop routes, determine first and last points based on direction
      // To Project: Acc1 → Acc2 → ... → Project (first acc is origin, project is destination)
      // To Accommodation: Project → Acc1 → Acc2 → ... (project is origin, last acc is destination)
      const firstStop = accommodationStops[0];
      const lastStop = accommodationStops[accommodationStops.length - 1];
      const firstAcc = firstStop?.accommodationDetails;
      const lastAcc = lastStop?.accommodationDetails;

      // Build waypoints array with all stops (for route reconstruction)
      const waypoints = [];
      let sequence = 1;

      if (isToProject) {
        // To Project: Acc1 → Acc2 → ... → Project
        accommodationStops.forEach((stop) => {
          const acc = stop.accommodationDetails;
          waypoints.push({
            sequence: sequence++,
            type: 'accommodation',
            name: stop.accommodation_name,
            accommodation_id: stop.accommodation_id,
            lat: roundCoord(acc?.latitude),
            lon: roundCoord(acc?.longitude),
            address: acc ? `${acc.address || ''}, ${acc.city || ''}` : '',
            passenger_ids: stop.passengers.map(p => p.user_id || p.id)
          });
        });
        // Final destination: Project
        waypoints.push({
          sequence: sequence++,
          type: 'worksite',
          name: project?.name || 'Work Site',
          lat: roundCoord(project?.latitude),
          lon: roundCoord(project?.longitude),
          address: project ? `${project.street_address || ''}, ${project.city || ''}` : '',
          passenger_ids: [] // All passengers get off here
        });
      } else {
        // To Accommodation: Project → Acc1 → Acc2 → ...
        waypoints.push({
          sequence: sequence++,
          type: 'worksite',
          name: project?.name || 'Work Site',
          lat: roundCoord(project?.latitude),
          lon: roundCoord(project?.longitude),
          address: project ? `${project.street_address || ''}, ${project.city || ''}` : '',
          passenger_ids: allPassengers.map(p => p.user_id || p.id) // All passengers get on here
        });
        accommodationStops.forEach((stop) => {
          const acc = stop.accommodationDetails;
          waypoints.push({
            sequence: sequence++,
            type: 'accommodation',
            name: stop.accommodation_name,
            accommodation_id: stop.accommodation_id,
            lat: roundCoord(acc?.latitude),
            lon: roundCoord(acc?.longitude),
            address: acc ? `${acc.address || ''}, ${acc.city || ''}` : '',
            passenger_ids: stop.passengers.map(p => p.user_id || p.id) // Passengers who get off here
          });
        });
      }

      // Build route description for notes
      const routeDescription = waypoints.map(w => w.name).join(' → ');

      // Calculate end time based on departure + duration if not manually set
      let calculatedEndTime = scheduledEndTime;
      if (!calculatedEndTime && optimizedRoute?.total_duration_minutes && scheduledTime) {
        const [hours, minutes] = scheduledTime.split(':').map(Number);
        const totalMinutes = hours * 60 + minutes + Math.round(optimizedRoute.total_duration_minutes);
        const endHours = Math.floor(totalMinutes / 60) % 24;
        const endMinutes = totalMinutes % 60;
        calculatedEndTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
      }

      // Create ONE transport with simplified payload
      const payload = {
        project: selectedProject,
        transport_type: transportType,
        transport_date: transportDate,
        scheduled_time: scheduledTime,
        scheduled_end_time: calculatedEndTime,
        car: car.id,
        driver: driverId,
        passengers: allPassengers.map(p => p.user_id || p.id),
        waypoints: waypoints,
        route_geometry: optimizedRoute?.geometry || null,
        distance_km: optimizedRoute?.total_distance_km || null,
        duration_minutes: optimizedRoute?.total_duration_minutes || null,
        status: 'scheduled',
        notes: routeDescription,
        related_travel: null
      };

      // Check if we're editing an existing transport for this car
      const existingTransport = editingTransportId
        ? localTransports.find(t => t.id === editingTransportId && t.car === car.id)
        : null;

      let response;
      if (existingTransport) {
        // Update existing transport
        response = await axios.patch(
          `${API_BASE_URL}/api/local-transports/${existingTransport.id}/`,
          payload,
          { headers }
        );
        // Update in state
        setLocalTransports(prev => prev.map(t =>
          t.id === existingTransport.id ? response.data : t
        ));
      } else {
        // Create new transport
        response = await axios.post(`${API_BASE_URL}/api/local-transports/`, payload, { headers });
        // Add to state
        setLocalTransports(prev => [...prev, response.data]);
      }

      // Remove passengers from car assignment
      setDailyCarAssignments(prev => {
        const tabAssignments = prev[tabKey] || {};
        const { [car.id]: removed, ...restOfTab } = tabAssignments;
        return {
          ...prev,
          [tabKey]: restOfTab
        };
      });

      // Remove from pending lists (only for new transports, not edits)
      if (!existingTransport) {
        const allPassengerIds = allPassengers.map(p => p.user_id || p.id);
        if (isToProject) {
          setPendingToProject(prev => prev.filter(p => !allPassengerIds.includes(p.user_id)));
        } else {
          setPendingToAccommodation(prev => prev.filter(p => !allPassengerIds.includes(p.user_id)));
        }
      }

      // Reset states
      setActiveCarId(null);
      setEditingTransportId(null);
      // Trigger refresh to reload pending people lists
      setRefreshTrigger(prev => prev + 1);

    } catch (error) {
      console.error('Error saving daily route:', error);
      alert('Failed to save route: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Get all cars with their scheduled transports for this date
  // Cars can have multiple transports per day (no filtering - just show all)
  const getAvailableCars = useMemo(() => {
    // Return all cars - a car can do multiple trips per day
    // Each car will show its scheduled transports for visibility
    return cars.map(car => {
      const carTransports = localTransports.filter(t => t.car === car.id);
      return {
        ...car,
        scheduledTransports: carTransports,
        hasScheduledTransports: carTransports.length > 0
      };
    });
  }, [cars, localTransports]);

  // Get all person IDs currently assigned to any car (for current tab)
  const assignedPersonIds = useMemo(() => {
    const ids = new Set();
    const tabAssignments = carAssignments[airportSubTab] || {};
    Object.values(tabAssignments).forEach(passengers => {
      passengers.forEach(p => {
        const personId = p.user_id || p.id;
        if (personId) ids.add(personId);
      });
    });
    return ids;
  }, [carAssignments, airportSubTab]);

  // Check if a person is assigned to any car (Airport Transfers)
  const isPersonAssigned = (person) => {
    const personId = person.user_id || person.id;
    return assignedPersonIds.has(personId);
  };

  // Get daily key based on current dailySubTab
  const getDailyTabKey = () => {
    return dailySubTab === DAILY_SUB_TABS.TO_PROJECT ? 'to_project' : 'to_accommodation';
  };

  // Assigned daily person IDs (for Daily Planning)
  const assignedDailyPersonIds = useMemo(() => {
    const ids = new Set();
    const tabKey = getDailyTabKey();
    const tabAssignments = dailyCarAssignments[tabKey] || {};
    Object.values(tabAssignments).forEach(passengers => {
      passengers.forEach(p => {
        const personId = p.user_id || p.id;
        if (personId) ids.add(personId);
      });
    });
    return ids;
  }, [dailyCarAssignments, dailySubTab]);

  // Check if a person is assigned to any car (Daily Planning)
  const isDailyPersonAssigned = (person) => {
    const personId = person.user_id || person.id;
    return assignedDailyPersonIds.has(personId);
  };

  // Group car assignments - each car with its passengers and their accommodations info
  // Also includes validation warnings and proper ordering (driver's accommodation last)
  const carsWithPassengers = useMemo(() => {
    const result = [];
    const tabAssignments = carAssignments[airportSubTab] || {};
    const tabPickupTimes = carPickupTimes[airportSubTab] || {};

    Object.entries(tabAssignments).forEach(([carId, passengers]) => {
      if (passengers.length === 0) return;

      const car = cars.find(c => c.id === parseInt(carId));
      if (!car) return;

      // First passenger is the driver
      const driver = passengers[0];
      const driverId = driver?.user_id || driver?.id;
      const driverAccInfo = userAccommodations[driverId] || { accommodation_name: 'Unknown', accommodation_id: null };
      const driverAccName = driverAccInfo.accommodation_name;

      // Group passengers by accommodation for display
      const passengersByAccommodation = {};
      passengers.forEach(passenger => {
        const personId = passenger.user_id || passenger.id;
        const accInfo = userAccommodations[personId] || { accommodation_name: 'Unknown', accommodation_id: null };
        const accName = accInfo.accommodation_name;

        if (!passengersByAccommodation[accName]) {
          passengersByAccommodation[accName] = {
            accommodation_id: accInfo.accommodation_id,
            accommodation_name: accName,
            passengers: [],
            isDriverAccommodation: accName === driverAccName
          };
        }
        passengersByAccommodation[accName].passengers.push({
          ...passenger,
          accommodation_name: accName,
          accommodation_id: accInfo.accommodation_id,
          isDriver: personId === driverId
        });
      });

      // Sort accommodations based on transport type:
      // - Pickups: Driver's accommodation LAST (driver drops off passengers, then goes home)
      // - Dropoffs: Driver's accommodation FIRST (driver starts from home, picks up others)
      const isDropoffMode = mainTab === MAIN_TABS.AIRPORT_TRANSFERS && airportSubTab === AIRPORT_SUB_TABS.DROPOFFS;

      const sortedAccommodations = Object.entries(passengersByAccommodation)
        .sort((a, b) => {
          if (isDropoffMode) {
            // For dropoffs: Driver's accommodation goes FIRST (start point)
            if (a[1].isDriverAccommodation && !b[1].isDriverAccommodation) return -1;
            if (!a[1].isDriverAccommodation && b[1].isDriverAccommodation) return 1;
          } else {
            // For pickups: Driver's accommodation goes LAST (end point)
            if (a[1].isDriverAccommodation && !b[1].isDriverAccommodation) return 1;
            if (!a[1].isDriverAccommodation && b[1].isDriverAccommodation) return -1;
          }
          // Otherwise sort alphabetically
          return a[0].localeCompare(b[0]);
        })
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});

      // Validation: Check if all passengers arrive at the same airport (for pickups)
      const arrivalAirports = passengers
        .map(p => p.arrival_airport)
        .filter(Boolean);
      const uniqueAirports = [...new Set(arrivalAirports)];
      const sameAirportWarning = uniqueAirports.length > 1;
      const primaryAirport = uniqueAirports[0] || null;

      // Validation: Check if all passengers depart from the same airport (for dropoffs)
      const departureAirports = passengers
        .map(p => p.departure_airport)
        .filter(Boolean);
      const uniqueDepartureAirports = [...new Set(departureAirports)];
      const sameDepartureAirportWarning = uniqueDepartureAirports.length > 1;
      const primaryDepartureAirport = uniqueDepartureAirports[0] || null;

      // Validation: Check if pickup time is after all arrival times (for pickups)
      const pickupTime = tabPickupTimes[carId] || '08:00';
      const arrivalTimes = passengers
        .map(p => p.arrival_time)
        .filter(Boolean)
        .map(t => t.slice(0, 5)); // Get HH:mm format
      const latestArrival = arrivalTimes.length > 0
        ? arrivalTimes.reduce((latest, t) => t > latest ? t : latest, '00:00')
        : null;
      const pickupBeforeArrivalWarning = latestArrival && pickupTime < latestArrival;

      // Validation: For dropoffs, check if pickup time is before earliest departure
      const departureTimes = passengers
        .map(p => p.departure_time)
        .filter(Boolean)
        .map(t => t.slice(0, 5));
      const earliestDeparture = departureTimes.length > 0
        ? departureTimes.reduce((earliest, t) => t < earliest ? t : earliest, '23:59')
        : null;

      // For dropoffs: warn if pickup time is AFTER earliest departure (too late!)
      const pickupAfterDepartureWarning = isDropoffMode && earliestDeparture && pickupTime >= earliestDeparture;

      // Calculate recommended pickup time for dropoffs
      // Uses route duration from optimization if available, otherwise estimates 2h30min
      let recommendedDropoffPickupTime = null;
      const routeData = optimizedRoutes[airportSubTab]?.[carId];
      const routeDuration = routeData?.total_duration_minutes || 0;
      if (earliestDeparture) {
        recommendedDropoffPickupTime = calculateRecommendedDropoffTime(earliestDeparture, routeDuration);
      }

      // Check if end time is manually set and if it's realistic
      const isManualEndTime = manualEndTimes[airportSubTab]?.[carId] || false;
      const currentEndTime = carEndTimes[airportSubTab]?.[carId];
      let expectedEndTime = null;
      let manualEndTimeUnrealistic = false;

      if (pickupTime && routeDuration > 0) {
        // Calculate expected end time based on start time + route duration
        const [startH, startM] = pickupTime.split(':').map(Number);
        const expectedTotalMins = startH * 60 + startM + routeDuration;
        const expectedH = Math.floor(expectedTotalMins / 60) % 24;
        const expectedM = expectedTotalMins % 60;
        expectedEndTime = `${String(expectedH).padStart(2, '0')}:${String(expectedM).padStart(2, '0')}`;

        // Check if manual end time differs significantly (more than 15 min earlier)
        if (isManualEndTime && currentEndTime) {
          const [endH, endM] = currentEndTime.split(':').map(Number);
          const manualTotalMins = endH * 60 + endM;
          // Warning if manual time is more than 15 minutes earlier than expected
          if (manualTotalMins < expectedTotalMins - 15) {
            manualEndTimeUnrealistic = true;
          }
        }
      }

      // Check if arrival at airport is less than 2h before earliest departure (for dropoffs)
      let arrivalTooLate = false;
      let arrivalTimeAtAirport = currentEndTime || expectedEndTime;
      if (isDropoffMode && earliestDeparture && arrivalTimeAtAirport) {
        const [arrH, arrM] = arrivalTimeAtAirport.split(':').map(Number);
        const [depH, depM] = earliestDeparture.split(':').map(Number);
        const arrivalMins = arrH * 60 + arrM;
        const departureMins = depH * 60 + depM;
        // Warning if arrival is less than 2h (120 min) before departure
        if (arrivalMins > departureMins - 120) {
          arrivalTooLate = true;
        }
      }

      result.push({
        carId: parseInt(carId),
        car,
        passengers,
        passengersByAccommodation: sortedAccommodations,
        driver,
        driverAccommodation: driverAccName,
        isDropoffMode,
        // Validation info
        warnings: {
          differentAirports: sameAirportWarning,
          differentDepartureAirports: sameDepartureAirportWarning,
          pickupBeforeArrival: pickupBeforeArrivalWarning,
          pickupAfterDeparture: pickupAfterDepartureWarning,
          latestArrival,
          earliestDeparture,
          recommendedDropoffPickupTime,
          isManualEndTime,
          expectedEndTime,
          manualEndTimeUnrealistic,
          routeDuration,
          arrivalTooLate,
          arrivalTimeAtAirport
        },
        airportInfo: {
          arrivalAirport: primaryAirport,
          arrivalAirportCoords: getAirportCoordinates(primaryAirport),
          departureAirport: primaryDepartureAirport,
          departureAirportCoords: getAirportCoordinates(primaryDepartureAirport),
          allArrivalAirports: uniqueAirports,
          allDepartureAirports: uniqueDepartureAirports
        }
      });
    });

    return result;
  }, [carAssignments, cars, userAccommodations, carPickupTimes, carEndTimes, manualEndTimes, mainTab, airportSubTab, optimizedRoutes]);

  // Filter transports by type for current tab
  const filteredTransports = useMemo(() => {
    if (mainTab === MAIN_TABS.AIRPORT_TRANSFERS) {
      if (airportSubTab === AIRPORT_SUB_TABS.PICKUPS) {
        return localTransports.filter(t => t.transport_type === 'airport_pickup');
      } else {
        return localTransports.filter(t => t.transport_type === 'airport_dropoff');
      }
    } else {
      // Daily Planning
      if (dailySubTab === DAILY_SUB_TABS.TO_PROJECT) {
        return localTransports.filter(t => t.transport_type === 'daily_to_work');
      } else {
        return localTransports.filter(t =>
          t.transport_type === 'daily_to_home' || t.transport_type === 'daily_from_work'
        );
      }
    }
  }, [localTransports, mainTab, airportSubTab, dailySubTab]);

  // Sorted pending pickups - by arrival time, then by accommodation name
  const sortedPendingPickups = useMemo(() => {
    return [...pendingPickups].sort((a, b) => {
      // Primary sort: arrival_time
      const timeA = a.arrival_time || '99:99';
      const timeB = b.arrival_time || '99:99';
      if (timeA !== timeB) {
        return timeA.localeCompare(timeB);
      }
      // Secondary sort: accommodation name
      const accA = userAccommodations[a.user_id]?.accommodation_name || 'ZZZ';
      const accB = userAccommodations[b.user_id]?.accommodation_name || 'ZZZ';
      return accA.localeCompare(accB);
    });
  }, [pendingPickups, userAccommodations]);

  // Sorted pending dropoffs - by departure time, then by accommodation name
  const sortedPendingDropoffs = useMemo(() => {
    return [...pendingDropoffs].sort((a, b) => {
      // Primary sort: departure_time
      const timeA = a.departure_time || '99:99';
      const timeB = b.departure_time || '99:99';
      if (timeA !== timeB) {
        return timeA.localeCompare(timeB);
      }
      // Secondary sort: accommodation name
      const accA = userAccommodations[a.user_id]?.accommodation_name || 'ZZZ';
      const accB = userAccommodations[b.user_id]?.accommodation_name || 'ZZZ';
      return accA.localeCompare(accB);
    });
  }, [pendingDropoffs, userAccommodations]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-sm text-slate-600">Loading...</span>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex-1 flex flex-col p-3 bg-steel-50 min-h-0 overflow-hidden">
          {/* Main Tabs */}
            <div className="mb-3 bg-white rounded-lg border border-steel-200 p-1">
              <div className="flex space-x-1">
                <button
                  onClick={() => setMainTab(MAIN_TABS.DAILY_PLANNING)}
                  className={`flex-1 flex items-center justify-center space-x-1.5 px-3 py-2 rounded text-sm font-medium transition-colors ${
                    mainTab === MAIN_TABS.DAILY_PLANNING ? 'bg-brand-500 text-white' : 'text-slate-600 hover:bg-steel-100'
                  }`}
                >
                  <Car className="w-4 h-4" />
                  <span>Daily Planning</span>
                  {(pendingToProject.length > 0 || pendingToAccommodation.length > 0) && (
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                      mainTab === MAIN_TABS.DAILY_PLANNING ? 'bg-brand-500' : 'bg-brand-100 text-brand-800'
                    }`}>{pendingToProject.length + pendingToAccommodation.length}</span>
                  )}
                </button>
                <button
                  onClick={() => setMainTab(MAIN_TABS.AIRPORT_TRANSFERS)}
                  className={`flex-1 flex items-center justify-center space-x-1.5 px-3 py-2 rounded text-sm font-medium transition-colors ${
                    mainTab === MAIN_TABS.AIRPORT_TRANSFERS ? 'bg-purple-600 text-white' : 'text-slate-600 hover:bg-steel-100'
                  }`}
                >
                  <Plane className="w-4 h-4" />
                  <span>Airport Transfers</span>
                  {(pendingPickups.length > 0 || pendingDropoffs.length > 0) && (
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                      mainTab === MAIN_TABS.AIRPORT_TRANSFERS ? 'bg-purple-500' : 'bg-purple-100 text-purple-800'
                    }`}>{pendingPickups.length + pendingDropoffs.length}</span>
                  )}
                </button>
              </div>
            </div>

            {/* Sub Tabs */}
            {mainTab === MAIN_TABS.DAILY_PLANNING && (
              <div className="mb-3 bg-steel-50 rounded-lg border border-steel-200 p-1">
                <div className="flex space-x-1">
                  <button
                    onClick={() => {
                      setDailySubTab(DAILY_SUB_TABS.TO_PROJECT);
                      setActiveCarId(null); // Reset map to overview when switching tabs
                    }}
                    className={`flex-1 flex items-center justify-center space-x-1.5 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                      dailySubTab === DAILY_SUB_TABS.TO_PROJECT ? 'bg-brand-500 text-white' : 'text-slate-600 hover:bg-steel-100'
                    }`}
                  >
                    <ArrowRight className="w-3.5 h-3.5" />
                    <span>To Project</span>
                    {pendingToProject.length > 0 && (
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                        dailySubTab === DAILY_SUB_TABS.TO_PROJECT ? 'bg-blue-400' : 'bg-brand-100 text-brand-800'
                      }`}>{pendingToProject.length}</span>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setDailySubTab(DAILY_SUB_TABS.TO_ACCOMMODATION);
                      setActiveCarId(null); // Reset map to overview when switching tabs
                    }}
                    className={`flex-1 flex items-center justify-center space-x-1.5 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                      dailySubTab === DAILY_SUB_TABS.TO_ACCOMMODATION ? 'bg-indigo-500 text-white' : 'text-slate-600 hover:bg-steel-100'
                    }`}
                  >
                    <Home className="w-3.5 h-3.5" />
                    <span>To Accommodation</span>
                    {pendingToAccommodation.length > 0 && (
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                        dailySubTab === DAILY_SUB_TABS.TO_ACCOMMODATION ? 'bg-indigo-400' : 'bg-indigo-100 text-indigo-800'
                      }`}>{pendingToAccommodation.length}</span>
                    )}
                  </button>
                </div>
              </div>
            )}

            {mainTab === MAIN_TABS.AIRPORT_TRANSFERS && (
              <div className="mb-3 bg-steel-50 rounded-lg border border-steel-200 p-1">
                <div className="flex space-x-1">
                  <button
                    onClick={() => {
                      setAirportSubTab(AIRPORT_SUB_TABS.PICKUPS);
                      setActiveCarId(null); // Reset map to overview when switching tabs
                    }}
                    className={`flex-1 flex items-center justify-center space-x-1.5 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                      airportSubTab === AIRPORT_SUB_TABS.PICKUPS ? 'bg-green-500 text-white' : 'text-slate-600 hover:bg-steel-100'
                    }`}
                  >
                    <Plane className="w-3.5 h-3.5" />
                    <span>Pickups</span>
                    {pendingPickups.length > 0 && (
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                        airportSubTab === AIRPORT_SUB_TABS.PICKUPS ? 'bg-green-400' : 'bg-green-100 text-green-800'
                      }`}>{pendingPickups.length}</span>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setAirportSubTab(AIRPORT_SUB_TABS.DROPOFFS);
                      setActiveCarId(null); // Reset map to overview when switching tabs
                    }}
                    className={`flex-1 flex items-center justify-center space-x-1.5 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                      airportSubTab === AIRPORT_SUB_TABS.DROPOFFS ? 'bg-orange-500 text-white' : 'text-slate-600 hover:bg-steel-100'
                    }`}
                  >
                    <Plane className="w-3.5 h-3.5 transform rotate-45" />
                    <span>Dropoffs</span>
                    {pendingDropoffs.length > 0 && (
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                        airportSubTab === AIRPORT_SUB_TABS.DROPOFFS ? 'bg-orange-400' : 'bg-orange-100 text-orange-800'
                      }`}>{pendingDropoffs.length}</span>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 flex-1 min-h-0 overflow-hidden">
              {/* Left Column - Pending Transport Needs */}
              <div className="bg-white rounded-lg border border-steel-200 flex flex-col min-h-0 overflow-hidden">
                <div className="px-3 py-2 border-b border-steel-200">
                  <h2 className="text-xs font-semibold text-slate-900 flex items-center justify-between">
                    <span className="flex items-center">
                      <AlertCircle className="w-3.5 h-3.5 mr-1.5 text-yellow-500" />
                      {activeTab === TRANSPORT_TABS.AIRPORT_PICKUPS && 'Arrivals'}
                      {activeTab === TRANSPORT_TABS.AIRPORT_DROPOFFS && 'Departures'}
                      {mainTab === MAIN_TABS.DAILY_PLANNING && (
                        dailySubTab === DAILY_SUB_TABS.TO_PROJECT
                          ? 'People → Project'
                          : 'People → Accommodation'
                      )}
                    </span>
                    {activeTab === TRANSPORT_TABS.AIRPORT_PICKUPS && pendingPickups.length > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-800 rounded-full">
                        {pendingPickups.length}
                      </span>
                    )}
                    {mainTab === MAIN_TABS.DAILY_PLANNING && dailySubTab === DAILY_SUB_TABS.TO_PROJECT && pendingToProject.length > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-brand-100 text-brand-800 rounded-full">
                        {pendingToProject.length}
                      </span>
                    )}
                    {mainTab === MAIN_TABS.DAILY_PLANNING && dailySubTab === DAILY_SUB_TABS.TO_ACCOMMODATION && pendingToAccommodation.length > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-indigo-100 text-indigo-800 rounded-full">
                        {pendingToAccommodation.length}
                      </span>
                    )}
                    {activeTab === TRANSPORT_TABS.AIRPORT_DROPOFFS && pendingDropoffs.length > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-800 rounded-full">
                        {pendingDropoffs.length}
                      </span>
                    )}
                  </h2>
                </div>

                <div className="p-2 space-y-1.5 flex-1 min-h-0 overflow-y-auto">
                  {activeTab === TRANSPORT_TABS.AIRPORT_PICKUPS && (
                    sortedPendingPickups.length > 0 ? (
                      sortedPendingPickups.map(person => {
                        const userAcc = userAccommodations[person.user_id];
                        const hasValidAccommodation = userAcc && userAcc.accommodation_id;
                        return (
                          <DraggablePerson
                            key={person.travel_id}
                            person={person}
                            type="airport_pickup"
                            isAssigned={isPersonAssigned(person)}
                            accommodation={hasValidAccommodation ? userAcc : null}
                            selectedProject={selectedProject}
                            onNavigateToAccommodation={!hasValidAccommodation ? handleNavigateToAccommodation : null}
                          />
                        );
                      })
                    ) : (
                      <div className="text-center py-4 text-slate-400 text-xs">
                        <CheckCircle className="w-5 h-5 mx-auto mb-1 text-green-300" />
                        <p>All assigned</p>
                      </div>
                    )
                  )}

                  {activeTab === TRANSPORT_TABS.AIRPORT_DROPOFFS && (
                    sortedPendingDropoffs.length > 0 ? (
                      sortedPendingDropoffs.map(person => {
                        const userAcc = userAccommodations[person.user_id];
                        const hasValidAccommodation = userAcc && userAcc.accommodation_id;
                        return (
                          <DraggablePerson
                            key={person.travel_id}
                            person={person}
                            type="airport_dropoff"
                            isAssigned={isPersonAssigned(person)}
                            accommodation={hasValidAccommodation ? userAcc : null}
                            selectedProject={selectedProject}
                            onNavigateToAccommodation={!hasValidAccommodation ? handleNavigateToAccommodation : null}
                          />
                        );
                      })
                    ) : (
                      <div className="text-center py-4 text-slate-400 text-xs">
                        <CheckCircle className="w-5 h-5 mx-auto mb-1 text-green-300" />
                        <p>All assigned</p>
                      </div>
                    )
                  )}

                  {mainTab === MAIN_TABS.DAILY_PLANNING && dailySubTab === DAILY_SUB_TABS.TO_PROJECT && (
                    pendingToProject.length > 0 ? (
                      pendingToProject.map(person => {
                        const currentProject = projects.find(p => p.id === parseInt(selectedProject));
                        const hasAccommodation = person.accommodation_name && person.accommodation_name !== 'No accommodation';
                        return (
                          <DraggablePerson
                            key={person.user_id}
                            person={person}
                            type="daily_to_work"
                            isAssigned={isDailyPersonAssigned(person)}
                            accommodation={hasAccommodation ? {
                              accommodation_name: person.accommodation_name,
                              room_name: person.room_name,
                              address: person.address,
                              city: person.city,
                              post_code: person.post_code,
                              country: person.country,
                              phone: person.phone
                            } : null}
                            projectName={currentProject?.name}
                            selectedProject={selectedProject}
                            onNavigateToAccommodation={!hasAccommodation ? handleNavigateToAccommodation : null}
                          />
                        );
                      })
                    ) : (
                      <div className="text-center py-4 text-slate-400 text-xs">
                        <CheckCircle className="w-5 h-5 mx-auto mb-1 text-green-300" />
                        <p>All assigned or no people on project</p>
                      </div>
                    )
                  )}

                  {mainTab === MAIN_TABS.DAILY_PLANNING && dailySubTab === DAILY_SUB_TABS.TO_ACCOMMODATION && (
                    pendingToAccommodation.length > 0 ? (
                      pendingToAccommodation.map(person => {
                        const currentProject = projects.find(p => p.id === parseInt(selectedProject));
                        const hasAccommodation = person.accommodation_name && person.accommodation_name !== 'No accommodation';
                        return (
                          <DraggablePerson
                            key={person.user_id}
                            person={person}
                            type="daily_to_home"
                            isAssigned={isDailyPersonAssigned(person)}
                            accommodation={hasAccommodation ? {
                              accommodation_name: person.accommodation_name,
                              room_name: person.room_name,
                              address: person.address,
                              city: person.city,
                              post_code: person.post_code,
                              country: person.country,
                              phone: person.phone
                            } : null}
                            projectName={currentProject?.name}
                            selectedProject={selectedProject}
                            onNavigateToAccommodation={!hasAccommodation ? handleNavigateToAccommodation : null}
                          />
                        );
                      })
                    ) : (
                      <div className="text-center py-4 text-slate-400 text-xs">
                        <CheckCircle className="w-5 h-5 mx-auto mb-1 text-green-300" />
                        <p>All assigned or no people on project</p>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Middle Column - Cars & Route Creation */}
              <div className="bg-white rounded-lg border border-steel-200 flex flex-col min-h-0 overflow-hidden">
                <div className="px-3 py-2 border-b border-steel-200">
                  <h2 className="text-xs font-semibold text-slate-900 flex items-center justify-between">
                    <span className="flex items-center">
                      <Car className="w-3.5 h-3.5 mr-1.5 text-green-600" />
                      Cars & Assignments
                    </span>
                    {getAvailableCars.length > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-800 rounded-full">
                        {getAvailableCars.length}
                      </span>
                    )}
                  </h2>
                </div>

                <div className="p-2 space-y-2 flex-1 min-h-0 overflow-y-auto">
                  {getAvailableCars.length > 0 ? (
                    getAvailableCars.map(car => {
                      // For Airport Transfers use carAssignments, for Daily Planning use dailyCarAssignments
                      const isAirportMode = mainTab === MAIN_TABS.AIRPORT_TRANSFERS;
                      const tabAssignments = isAirportMode
                        ? (carAssignments[airportSubTab] || {})
                        : (dailyCarAssignments[getDailyTabKey()] || {});
                      const carPassengers = tabAssignments[car.id] || [];
                      const carData = isAirportMode ? carsWithPassengers.find(c => c.carId === car.id) : null;
                      const hasPassengers = carPassengers.length > 0;
                      const isActive = activeCarId === car.id;

                      return (
                        <div
                          key={car.id}
                          className={`border-2 rounded transition-all cursor-pointer ${
                            isActive
                              ? 'border-brand-500 ring-2 ring-brand-200 bg-brand-50'
                              : hasPassengers
                                ? 'border-green-400 bg-green-50 hover:border-green-500'
                                : 'border-steel-200 bg-white hover:border-steel-300'
                          }`}
                          onClick={() => {
                            if (hasPassengers) {
                              setActiveCarId(car.id);
                              setViewingTransportId(null);
                            }
                          }}
                        >
                          {/* Active indicator */}
                          {hasPassengers && (
                            <div className={`px-2 py-0.5 text-[9px] font-medium flex items-center justify-between ${
                              isActive ? 'bg-brand-500 text-white' : 'bg-steel-100 text-slate-500'
                            }`}>
                              <span>{isActive ? '📍 Showing on map' : 'Click to show on map'}</span>
                              {isActive && <span className="text-blue-200">●</span>}
                            </div>
                          )}
                          {/* Scheduled transports indicator */}
                          {car.hasScheduledTransports && car.scheduledTransports.length > 0 && (
                            <div className="px-2 py-1 bg-orange-50 border-b border-orange-200">
                              <div className="text-[9px] font-medium text-orange-700 flex items-center">
                                <Clock className="w-3 h-3 mr-1" />
                                {car.scheduledTransports.length} scheduled trip{car.scheduledTransports.length > 1 ? 's' : ''} today:
                              </div>
                              <div className="mt-0.5 space-y-0.5">
                                {car.scheduledTransports.map((transport, idx) => {
                                  const startTime = transport.scheduled_time?.substring(0, 5) || '??:??';
                                  const endTime = transport.scheduled_end_time?.substring(0, 5);
                                  const timeDisplay = endTime ? `${startTime}→${endTime}` : startTime;
                                  const typeIcon = transport.transport_type === 'airport_pickup' ? '✈️→🏠'
                                    : transport.transport_type === 'airport_dropoff' ? '🏠→✈️'
                                    : transport.transport_type === 'daily_to_work' ? '🏠→🏗️'
                                    : '🏗️→🏠';
                                  // Get origin/destination from waypoints (new model) or fallback to computed fields
                                  const originName = transport.waypoints?.[0]?.name || transport.origin?.name || '?';
                                  const destName = transport.waypoints?.[transport.waypoints?.length - 1]?.name || transport.destination?.name || '?';
                                  return (
                                    <div
                                      key={transport.id}
                                      className="text-[9px] text-orange-600 flex items-center cursor-pointer hover:text-orange-800"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setViewingTransportId(transport.id);
                                        setActiveCarId(null);
                                      }}
                                    >
                                      <span className="w-3 text-center">{idx + 1}.</span>
                                      <span className="ml-1">
                                        <span className="font-medium">{timeDisplay}</span> {typeIcon} {' '}
                                        {originName.substring(0, 12)} → {destName.substring(0, 12)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {/* Car drop zone */}
                          <DraggableCar
                            car={car}
                            passengers={carPassengers}
                            onRemovePassenger={(person) => isAirportMode
                              ? handleRemovePersonFromCar(car.id, person)
                              : handleRemoveDailyPersonFromCar(car.id, person)
                            }
                            onPersonDrop={isAirportMode ? handlePersonDropOnCar : handleDailyPersonDropOnCar}
                            availablePeople={(() => {
                              if (isAirportMode) {
                                // Get pending people based on active tab
                                const pendingPeople = airportSubTab === AIRPORT_SUB_TABS.PICKUPS
                                  ? pendingPickups
                                  : pendingDropoffs;
                                // Filter out people already assigned to ANY car (for current tab)
                                const currentTabAssignments = carAssignments[airportSubTab] || {};
                                const allAssignedIds = Object.values(currentTabAssignments)
                                  .flat()
                                  .map(p => p.id || p.user_id);
                                return pendingPeople.filter(p => {
                                  const personId = p.id || p.user_id;
                                  return !allAssignedIds.includes(personId);
                                });
                              } else {
                                // Daily Planning mode
                                const pendingPeople = dailySubTab === DAILY_SUB_TABS.TO_PROJECT
                                  ? pendingToProject
                                  : pendingToAccommodation;
                                const currentTabAssignments = dailyCarAssignments[getDailyTabKey()] || {};
                                const allAssignedIds = Object.values(currentTabAssignments)
                                  .flat()
                                  .map(p => p.id || p.user_id);
                                return pendingPeople.filter(p => {
                                  const personId = p.id || p.user_id;
                                  return !allAssignedIds.includes(personId);
                                });
                              }
                            })()}
                            userAccommodations={userAccommodations}
                          />

                          {/* Copy from recent day button - shown for Daily Planning when car has no passengers */}
                          {!hasPassengers && !isAirportMode && (() => {
                            const transportType = dailySubTab === DAILY_SUB_TABS.TO_PROJECT ? 'daily_to_work' : 'daily_to_home';
                            const copyStatus = getCopyFromRecentDayStatus(car.id, transportType);
                            const buttonLabel = copyStatus.dayLabel
                              ? `Copy from ${copyStatus.dayLabel}`
                              : 'Copy from recent';
                            return (
                              <div className="border-t border-steel-200 bg-steel-50 px-2 py-1.5 rounded-b">
                                <button
                                  onClick={() => copyStatus.canCopy && copyFromRecentDay(car.id, transportType)}
                                  disabled={!copyStatus.canCopy}
                                  className={`w-full text-[10px] py-1 px-2 border rounded flex items-center justify-center transition-colors ${
                                    copyStatus.canCopy
                                      ? 'bg-brand-100 hover:bg-brand-200 text-brand-700 border-brand-300 cursor-pointer'
                                      : 'bg-steel-100 text-slate-400 border-steel-200 cursor-not-allowed'
                                  }`}
                                  title={copyStatus.reason}
                                >
                                  <Copy className="w-3 h-3 mr-1" />
                                  {buttonLabel}
                                </button>
                              </div>
                            );
                          })()}

                          {/* If car has passengers, show accommodation grouping and create button */}
                          {hasPassengers && carData && (
                            <div className="border-t border-green-300 bg-white rounded-b">
                              {/* Warnings */}
                              {mainTab === MAIN_TABS.AIRPORT_TRANSFERS && (
                                <div className="px-1.5 pt-1.5 space-y-1">
                                  {/* Different airports warning */}
                                  {airportSubTab === AIRPORT_SUB_TABS.PICKUPS && carData.warnings.differentAirports && (
                                    <div className="flex items-center text-[10px] px-2 py-1 bg-yellow-50 border border-yellow-300 rounded text-yellow-800">
                                      <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                                      <span>Different arrival airports: {carData.airportInfo.allArrivalAirports.join(', ')}</span>
                                    </div>
                                  )}
                                  {airportSubTab === AIRPORT_SUB_TABS.DROPOFFS && carData.warnings.differentDepartureAirports && (
                                    <div className="flex items-center text-[10px] px-2 py-1 bg-yellow-50 border border-yellow-300 rounded text-yellow-800">
                                      <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                                      <span>Different departure airports: {carData.airportInfo.allDepartureAirports.join(', ')}</span>
                                    </div>
                                  )}
                                  {/* Pickup before arrival warning (for pickups) */}
                                  {airportSubTab === AIRPORT_SUB_TABS.PICKUPS && carData.warnings.pickupBeforeArrival && (
                                    <div className="flex items-center text-[10px] px-2 py-1 bg-red-50 border border-red-300 rounded text-red-800">
                                      <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                                      <span>Pickup time before latest arrival ({carData.warnings.latestArrival})</span>
                                    </div>
                                  )}
                                  {/* Pickup after departure warning (for dropoffs) */}
                                  {airportSubTab === AIRPORT_SUB_TABS.DROPOFFS && carData.warnings.pickupAfterDeparture && (
                                    <div className="flex items-center text-[10px] px-2 py-1 bg-red-50 border border-red-300 rounded text-red-800">
                                      <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                                      <span>Start time after earliest departure ({carData.warnings.earliestDeparture})!</span>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Route optimization info */}
                              {(() => {
                                const tabKey = airportSubTab;
                                const routeData = optimizedRoutes[tabKey]?.[car.id];
                                const isOptimizing = routeOptimizing[car.id];

                                if (isOptimizing) {
                                  return (
                                    <div className="px-1.5 pt-1">
                                      <div className="flex items-center text-[10px] px-2 py-1.5 bg-brand-50 border border-brand-200 rounded text-brand-700">
                                        <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />
                                        <span>Optimizing route...</span>
                                      </div>
                                    </div>
                                  );
                                }

                                if (routeData) {
                                  return (
                                    <div className="px-1.5 pt-1">
                                      <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded px-2 py-1.5">
                                        <div className="flex items-center justify-between text-[10px]">
                                          <div className="flex items-center text-green-700">
                                            <Navigation className="w-3 h-3 mr-1" />
                                            <span className="font-semibold">{routeData.total_distance_km} km</span>
                                            <span className="mx-1.5 text-slate-400">•</span>
                                            <Clock className="w-3 h-3 mr-1" />
                                            <span className="font-semibold">{routeData.total_duration_minutes} min</span>
                                          </div>
                                          {routeData.time_saved_minutes > 0 && (
                                            <div className="flex items-center text-green-600 bg-green-100 px-1.5 py-0.5 rounded">
                                              <CheckCircle className="w-3 h-3 mr-0.5" />
                                              <span className="text-[9px] font-medium">-{routeData.time_saved_minutes} min saved</span>
                                            </div>
                                          )}
                                        </div>
                                        {routeData.optimized && routeData.optimal_order_names && routeData.optimal_order_names.length > 1 && (
                                          <div className="text-[9px] text-slate-500 mt-1 truncate">
                                            Optimal: {routeData.optimal_order_names.join(' → ')}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                }

                                return null;
                              })()}

                              {/* Route visualization: Airport → Stops */}
                              <div className="p-1.5 space-y-1">
                                {/* Airport start point (for pickups) */}
                                {mainTab === MAIN_TABS.AIRPORT_TRANSFERS && airportSubTab === AIRPORT_SUB_TABS.PICKUPS && carData.airportInfo.arrivalAirport && (
                                  <div className="bg-brand-50 rounded border border-brand-200 px-2 py-1">
                                    <div className="flex items-center text-[10px] text-brand-700">
                                      <span className="w-4 h-4 bg-brand-500 text-white rounded-full flex items-center justify-center text-[9px] mr-1.5">✈</span>
                                      <Plane className="w-3 h-3 mr-1" />
                                      <span className="font-medium">{carData.airportInfo.arrivalAirport}</span>
                                      {carData.warnings.latestArrival && (
                                        <span className="ml-auto text-brand-500">Last arrival: {carData.warnings.latestArrival}</span>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Accommodation stops */}
                                {(() => {
                                  const tabKey = airportSubTab;
                                  const routeData = optimizedRoutes[tabKey]?.[car.id];
                                  const stops = routeData?.stops || [];
                                  const segments = routeData?.segments || [];
                                  const tabPickupTimes = carPickupTimes[tabKey] || {};
                                  const startTime = tabPickupTimes[car.id];

                                  // Same color palette as map
                                  const segmentColors = [
                                    '#2563eb', // blue
                                    '#059669', // green
                                    '#7c3aed', // purple
                                    '#ea580c', // orange
                                    '#dc2626', // red
                                    '#0891b2', // cyan
                                    '#ca8a04', // yellow
                                    '#be185d', // pink
                                  ];

                                  // Helper to calculate arrival time
                                  const getArrivalTime = (cumulativeMinutes) => {
                                    if (!startTime || !cumulativeMinutes) return null;
                                    const [hours, minutes] = startTime.split(':').map(Number);
                                    const totalMinutes = hours * 60 + minutes + cumulativeMinutes;
                                    const arrHours = Math.floor(totalMinutes / 60) % 24;
                                    const arrMins = totalMinutes % 60;
                                    return `${String(arrHours).padStart(2, '0')}:${String(arrMins).padStart(2, '0')}`;
                                  };

                                  return Object.entries(carData.passengersByAccommodation).map(([accName, accData], idx) => {
                                    // Find stop info from optimization
                                    const stopInfo = stops.find(s => s.name === accName);
                                    const segmentInfo = segments[idx];
                                    const arrivalTime = stopInfo ? getArrivalTime(stopInfo.arrival_minutes) : null;
                                    const segmentColor = segmentColors[idx % segmentColors.length];

                                    return (
                                      <div key={accName} className="rounded border px-2 py-1 bg-steel-50 border-steel-200"
                                        style={{ borderLeftWidth: '4px', borderLeftColor: segmentColor }}
                                      >
                                        <div className="flex items-center text-[10px] text-slate-600">
                                          <span
                                            className="w-4 h-4 text-white rounded-full flex items-center justify-center text-[9px] font-bold mr-1.5"
                                            style={{ backgroundColor: segmentColor }}
                                          >
                                            {idx + 1}
                                          </span>
                                          <Home className="w-3 h-3 mr-1" style={{ color: segmentColor }} />
                                          <span className="font-medium truncate flex-1">{accName}</span>
                                          {accData.isDriverAccommodation && (
                                            <span className="ml-1 text-[9px] px-1 py-0.5 bg-green-200 text-green-800 rounded">Driver</span>
                                          )}
                                          <span className="ml-1 text-slate-400">({accData.passengers.length})</span>
                                        </div>
                                        {/* Arrival time and segment duration */}
                                        {(arrivalTime || segmentInfo) && (
                                          <div className="flex items-center justify-between mt-0.5 text-[9px] text-slate-500 pl-5">
                                            {segmentInfo && (
                                              <span style={{ color: segmentColor }}>
                                                +{segmentInfo.duration_minutes} min ({segmentInfo.distance_km} km)
                                              </span>
                                            )}
                                            {arrivalTime && (
                                              <span className="font-medium text-green-600">
                                                ETA: {arrivalTime}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  });
                                })()}

                                {/* Airport end point (for dropoffs) */}
                                {mainTab === MAIN_TABS.AIRPORT_TRANSFERS && airportSubTab === AIRPORT_SUB_TABS.DROPOFFS && carData.airportInfo.departureAirport && (
                                  <div className="bg-orange-50 rounded border border-orange-200 px-2 py-1">
                                    <div className="flex items-center text-[10px] text-orange-700">
                                      <span className="w-4 h-4 bg-orange-600 text-white rounded-full flex items-center justify-center text-[9px] mr-1.5">✈</span>
                                      <Plane className="w-3 h-3 mr-1" />
                                      <span className="font-medium">{carData.airportInfo.departureAirport}</span>
                                      {carData.warnings.earliestDeparture && (
                                        <span className="ml-auto text-orange-500">First departure: {carData.warnings.earliestDeparture}</span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Time picker and create button */}
                              <div className="px-1.5 pb-1.5 flex items-center gap-1">
                                <Clock className="w-3 h-3 text-slate-400" />
                                <input
                                  type="time"
                                  value={(carPickupTimes[airportSubTab] || {})[car.id] || '08:00'}
                                  onChange={(e) => {
                                    const newStartTime = e.target.value;
                                    // Update start time
                                    setCarPickupTimes(prev => ({
                                      ...prev,
                                      [airportSubTab]: {
                                        ...(prev[airportSubTab] || {}),
                                        [car.id]: newStartTime
                                      }
                                    }));
                                    // Auto-update end time if not manually set
                                    const isManual = manualEndTimes[airportSubTab]?.[car.id];
                                    const routeData = optimizedRoutes[airportSubTab]?.[car.id];
                                    if (!isManual && routeData?.total_duration_minutes) {
                                      const [hours, minutes] = newStartTime.split(':').map(Number);
                                      const totalMinutes = hours * 60 + minutes + routeData.total_duration_minutes;
                                      const endHours = Math.floor(totalMinutes / 60) % 24;
                                      const endMins = totalMinutes % 60;
                                      const endTime = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
                                      setCarEndTimes(prev => ({
                                        ...prev,
                                        [airportSubTab]: { ...(prev[airportSubTab] || {}), [car.id]: endTime }
                                      }));
                                    }
                                  }}
                                  className={`w-16 text-[10px] px-1 py-0.5 border rounded ${
                                    carData.warnings.pickupBeforeArrival || carData.warnings.pickupAfterDeparture
                                      ? 'border-red-400 bg-red-50'
                                      : 'border-steel-200'
                                  }`}
                                  title="Start time"
                                />
                                <span className="text-[10px] text-slate-400">→</span>
                                <input
                                  type="time"
                                  value={(carEndTimes[airportSubTab] || {})[car.id] || ''}
                                  onChange={(e) => {
                                    // Mark as manually set
                                    setManualEndTimes(prev => ({
                                      ...prev,
                                      [airportSubTab]: { ...(prev[airportSubTab] || {}), [car.id]: true }
                                    }));
                                    // Update end time
                                    setCarEndTimes(prev => ({
                                      ...prev,
                                      [airportSubTab]: {
                                        ...(prev[airportSubTab] || {}),
                                        [car.id]: e.target.value
                                      }
                                    }));
                                  }}
                                  className={`w-16 text-[10px] px-1 py-0.5 border rounded ${
                                    manualEndTimes[airportSubTab]?.[car.id] ? 'border-orange-400 bg-orange-50' : 'border-steel-200'
                                  }`}
                                  title={manualEndTimes[airportSubTab]?.[car.id] ? "End time (manually set)" : "End time (auto-calculated)"}
                                  placeholder="End"
                                />
                                {(() => {
                                  const stopCount = Object.keys(carData.passengersByAccommodation).length;
                                  const isMultiStop = stopCount > 1;
                                  const hasWarnings = carData.warnings.differentAirports ||
                                    carData.warnings.differentDepartureAirports ||
                                    carData.warnings.pickupBeforeArrival ||
                                    carData.warnings.pickupAfterDeparture ||
                                    carData.warnings.arrivalTooLate ||
                                    (carData.warnings.isManualEndTime && carData.warnings.manualEndTimeUnrealistic);
                                  const isEditingThisCar = editingTransportId && localTransports.find(t => t.id === editingTransportId)?.car === car.id;
                                  return (
                                    <>
                                      {/* Cancel button when editing */}
                                      {isEditingThisCar && (
                                        <button
                                          onClick={() => {
                                            const tabKey = airportSubTab === AIRPORT_SUB_TABS.PICKUPS ? 'pickups' : 'dropoffs';
                                            setCarAssignments(prev => ({
                                              ...prev,
                                              [tabKey]: {
                                                ...prev[tabKey],
                                                [car.id]: []
                                              }
                                            }));
                                            setEditingTransportId(null);
                                            setActiveCarId(null);
                                          }}
                                          className="text-[10px] py-1 px-2 text-white rounded flex items-center justify-center bg-steel-500 hover:bg-gray-600"
                                        >
                                          <X className="w-3 h-3 mr-1" />
                                          Cancel
                                        </button>
                                      )}
                                      <button
                                        onClick={() => createMultiStopRoute(car, carData.passengersByAccommodation)}
                                        className={`flex-1 text-[10px] py-1 text-white rounded flex items-center justify-center ${
                                          hasWarnings
                                            ? 'bg-yellow-600 hover:bg-yellow-700'
                                            : isMultiStop
                                              ? 'bg-purple-600 hover:bg-purple-700'
                                              : 'bg-brand-500 hover:bg-brand-600'
                                        }`}
                                        title={hasWarnings ? 'Save (warnings present)' : undefined}
                                      >
                                        <Navigation className="w-3 h-3 mr-1" />
                                        {isEditingThisCar ? 'Save' : (isMultiStop ? `Create (${stopCount} stops)` : 'Create Route')}
                                      </button>
                                    </>
                                  );
                                })()}
                              </div>

                              {/* Time-related warnings - shown below time inputs */}
                              {mainTab === MAIN_TABS.AIRPORT_TRANSFERS && airportSubTab === AIRPORT_SUB_TABS.DROPOFFS && (
                                <div className="px-1.5 pb-1.5 space-y-1">
                                  {/* Warning: arrival at airport less than 2h before departure */}
                                  {carData.warnings.arrivalTooLate && (
                                    <div className="flex items-center text-[10px] px-2 py-1 bg-red-50 border border-red-300 rounded text-red-800">
                                      <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                                      <span>
                                        Arrival at airport ({carData.warnings.arrivalTimeAtAirport}) is less than 2h before earliest departure ({carData.warnings.earliestDeparture})!
                                      </span>
                                    </div>
                                  )}
                                  {/* Warning: manually set end time may be unrealistic */}
                                  {carData.warnings.isManualEndTime && carData.warnings.manualEndTimeUnrealistic && (
                                    <div className="flex items-center text-[10px] px-2 py-1 bg-orange-50 border border-orange-300 rounded text-orange-800">
                                      <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                                      <span>
                                        Manual arrival time may be unrealistic (expected: {carData.warnings.expectedEndTime}, route: {carData.warnings.routeDuration} min)
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Daily Planning section - show when in Daily mode with passengers */}
                          {hasPassengers && !isAirportMode && (
                            <div className="border-t border-brand-300 bg-white rounded-b">
                              {/* Route optimization info */}
                              {(() => {
                                const tabKey = getDailyTabKey();
                                const routeData = dailyOptimizedRoutes[tabKey]?.[car.id];
                                const isOptimizing = routeOptimizing[car.id];

                                if (isOptimizing) {
                                  return (
                                    <div className="px-1.5 pt-1.5">
                                      <div className="flex items-center text-[10px] px-2 py-1.5 bg-brand-50 border border-brand-200 rounded text-brand-700">
                                        <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />
                                        <span>Optimizing route...</span>
                                      </div>
                                    </div>
                                  );
                                }

                                if (routeData) {
                                  return (
                                    <div className="px-1.5 pt-1.5">
                                      <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded px-2 py-1.5">
                                        <div className="flex items-center justify-between text-[10px]">
                                          <div className="flex items-center text-green-700">
                                            <Navigation className="w-3 h-3 mr-1" />
                                            <span className="font-semibold">{routeData.total_distance_km} km</span>
                                            <span className="mx-1.5 text-slate-400">•</span>
                                            <Clock className="w-3 h-3 mr-1" />
                                            <span className="font-semibold">{routeData.total_duration_minutes} min</span>
                                          </div>
                                          {routeData.time_saved_minutes > 0 && (
                                            <div className="flex items-center text-green-600 bg-green-100 px-1.5 py-0.5 rounded">
                                              <CheckCircle className="w-3 h-3 mr-0.5" />
                                              <span className="text-[9px] font-medium">-{routeData.time_saved_minutes} min saved</span>
                                            </div>
                                          )}
                                        </div>
                                        {routeData.optimized && routeData.optimal_order_names && routeData.optimal_order_names.length > 1 && (
                                          <div className="text-[9px] text-slate-500 mt-1 truncate">
                                            Optimal: {routeData.optimal_order_names.join(' → ')}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                }

                                return null;
                              })()}

                              {/* Route visualization */}
                              <div className="p-1.5 space-y-1">
                                {(() => {
                                  const isToProject = dailySubTab === DAILY_SUB_TABS.TO_PROJECT;
                                  const currentProject = projects.find(p => p.id === parseInt(selectedProject));
                                  const tabKey = getDailyTabKey();
                                  const routeData = dailyOptimizedRoutes[tabKey]?.[car.id];
                                  const stops = routeData?.stops || [];
                                  const segments = routeData?.segments || [];
                                  const tabPickupTimes = dailyCarPickupTimes[tabKey] || {};
                                  const startTime = tabPickupTimes[car.id] || (isToProject ? '07:00' : '16:00');

                                  // Group passengers by accommodation
                                  const passengersByAcc = {};
                                  carPassengers.forEach(p => {
                                    const accName = p.accommodation_name || 'No accommodation';
                                    if (!passengersByAcc[accName]) {
                                      passengersByAcc[accName] = [];
                                    }
                                    passengersByAcc[accName].push(p);
                                  });

                                  const accommodationStops = Object.entries(passengersByAcc);
                                  const segmentColors = [
                                    '#2563eb', '#059669', '#7c3aed', '#ea580c',
                                    '#dc2626', '#0891b2', '#ca8a04', '#be185d'
                                  ];

                                  // Helper to calculate arrival time
                                  const getArrivalTime = (cumulativeMinutes) => {
                                    if (!startTime || !cumulativeMinutes) return null;
                                    const [hours, minutes] = startTime.split(':').map(Number);
                                    const totalMinutes = hours * 60 + minutes + cumulativeMinutes;
                                    const arrHours = Math.floor(totalMinutes / 60) % 24;
                                    const arrMins = totalMinutes % 60;
                                    return `${String(arrHours).padStart(2, '0')}:${String(arrMins).padStart(2, '0')}`;
                                  };

                                  return (
                                    <>
                                      {/* Project start point (for To Accommodation) */}
                                      {!isToProject && currentProject && (
                                        <div className="bg-green-50 rounded border border-green-200 px-2 py-1">
                                          <div className="flex items-center text-[10px] text-green-700">
                                            <span className="w-4 h-4 bg-green-600 text-white rounded-full flex items-center justify-center text-[9px] mr-1.5">🏗️</span>
                                            <Building className="w-3 h-3 mr-1" />
                                            <span className="font-medium">{currentProject.name}</span>
                                            <span className="ml-auto text-green-500">Start: {startTime}</span>
                                          </div>
                                        </div>
                                      )}

                                      {/* Accommodation stops */}
                                      {accommodationStops.map(([accName, passengers], idx) => {
                                        const segmentColor = segmentColors[idx % segmentColors.length];
                                        // Find stop info from optimization
                                        const stopInfo = stops.find(s => s.name === accName);
                                        const segmentInfo = segments[idx];
                                        const arrivalTime = stopInfo ? getArrivalTime(stopInfo.arrival_minutes) : null;

                                        return (
                                          <div
                                            key={accName}
                                            className="rounded border px-2 py-1 bg-steel-50 border-steel-200"
                                            style={{ borderLeftWidth: '4px', borderLeftColor: segmentColor }}
                                          >
                                            <div className="flex items-center text-[10px] text-slate-700">
                                              <span
                                                className="w-4 h-4 text-white rounded-full flex items-center justify-center text-[9px] font-bold mr-1.5"
                                                style={{ backgroundColor: segmentColor }}
                                              >
                                                {idx + 1}
                                              </span>
                                              <Home className="w-3 h-3 mr-1" style={{ color: segmentColor }} />
                                              <span className="font-medium truncate max-w-[100px]">{accName}</span>
                                              <span className="ml-auto text-slate-400">
                                                {passengers.length} {passengers.length === 1 ? 'person' : 'people'}
                                              </span>
                                            </div>
                                            {/* Segment duration and ETA */}
                                            {(arrivalTime || segmentInfo) && (
                                              <div className="flex items-center justify-between mt-0.5 text-[9px] text-slate-500 pl-5">
                                                {segmentInfo && (
                                                  <span style={{ color: segmentColor }}>
                                                    +{segmentInfo.duration_minutes} min ({segmentInfo.distance_km} km)
                                                  </span>
                                                )}
                                                {arrivalTime && (
                                                  <span className="font-medium text-green-600">
                                                    ETA: {arrivalTime}
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                            <div className="mt-0.5 ml-5 flex flex-wrap gap-1">
                                              {passengers.map(p => (
                                                <span key={p.user_id} className="text-[9px] bg-brand-100 text-brand-700 px-1.5 py-0.5 rounded">
                                                  {p.first_name || p.user_name}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        );
                                      })}

                                      {/* Project end point (for To Project) */}
                                      {isToProject && currentProject && (
                                        <div className="bg-green-50 rounded border border-green-200 px-2 py-1">
                                          <div className="flex items-center text-[10px] text-green-700">
                                            <span className="w-4 h-4 bg-green-600 text-white rounded-full flex items-center justify-center text-[9px] mr-1.5">🏗️</span>
                                            <Building className="w-3 h-3 mr-1" />
                                            <span className="font-medium">{currentProject.name}</span>
                                            <span className="ml-auto text-green-500">
                                              {routeData ? `ETA: ${getArrivalTime(routeData.total_duration_minutes)}` : 'Destination'}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>

                              {/* Time controls and Save button */}
                              <div className="flex items-center space-x-1.5 p-1.5 pt-0">
                                <Clock className="w-3.5 h-3.5 text-slate-400" />
                                <input
                                  type="time"
                                  value={(dailyCarPickupTimes[getDailyTabKey()] || {})[car.id] || (dailySubTab === DAILY_SUB_TABS.TO_PROJECT ? '07:00' : '16:00')}
                                  onChange={(e) => setDailyCarPickupTimes(prev => ({
                                    ...prev,
                                    [getDailyTabKey()]: {
                                      ...(prev[getDailyTabKey()] || {}),
                                      [car.id]: e.target.value
                                    }
                                  }))}
                                  className="w-16 text-[10px] px-1 py-0.5 border border-steel-200 rounded"
                                  title="Start time"
                                />
                                <span className="text-[10px] text-slate-400">→</span>
                                <input
                                  type="time"
                                  value={(dailyCarEndTimes[getDailyTabKey()] || {})[car.id] || ''}
                                  onChange={(e) => setDailyCarEndTimes(prev => ({
                                    ...prev,
                                    [getDailyTabKey()]: {
                                      ...(prev[getDailyTabKey()] || {}),
                                      [car.id]: e.target.value
                                    }
                                  }))}
                                  className="w-16 text-[10px] px-1 py-0.5 border border-steel-200 rounded"
                                  title="End time (optional)"
                                  placeholder="End"
                                />
                                {/* Cancel button when editing */}
                                {editingTransportId && localTransports.find(t => t.id === editingTransportId)?.car === car.id && (
                                  <button
                                    onClick={() => {
                                      const tabKey = getDailyTabKey();
                                      setDailyCarAssignments(prev => ({
                                        ...prev,
                                        [tabKey]: {
                                          ...prev[tabKey],
                                          [car.id]: []
                                        }
                                      }));
                                      setEditingTransportId(null);
                                      setActiveCarId(null);
                                    }}
                                    className="text-[10px] py-1 px-2 text-white rounded flex items-center justify-center bg-steel-500 hover:bg-gray-600"
                                  >
                                    <X className="w-3 h-3 mr-1" />
                                    Cancel
                                  </button>
                                )}
                                <button
                                  onClick={() => createDailyRoute(car, carPassengers)}
                                  className="flex-1 text-[10px] py-1 text-white rounded flex items-center justify-center bg-brand-500 hover:bg-brand-600"
                                >
                                  <Navigation className="w-3 h-3 mr-1" />
                                  {editingTransportId && localTransports.find(t => t.id === editingTransportId)?.car === car.id ? 'Save' : 'Create Route'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-4 text-slate-400 text-xs">
                      <Car className="w-5 h-5 mx-auto mb-1" />
                      <p>No cars on this project for selected date</p>
                    </div>
                  )}

                  {/* Existing transports */}
                  {filteredTransports.length > 0 && (
                    <div className="pt-2 border-t border-steel-200 space-y-1.5">
                      <h3 className="text-[10px] font-medium text-slate-500 uppercase">Scheduled</h3>
                      {filteredTransports.map(transport => (
                        <TransportCard
                          key={transport.id}
                          transport={transport}
                          onDelete={deleteTransport}
                          onSelect={(t) => {
                            setViewingTransportId(viewingTransportId === t.id ? null : t.id);
                            setActiveCarId(null);
                            setEditingTransportId(null);
                          }}
                          onEdit={(t) => {
                            // Load transport into planning panel for full editing
                            loadTransportForEditing(t);
                          }}
                          isSelected={viewingTransportId === transport.id}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Route Map */}
              <div className="bg-white rounded-lg border border-steel-200 flex flex-col min-h-0 overflow-hidden">
                <div className="px-3 py-2 border-b border-steel-200">
                  <h2 className="text-xs font-semibold text-slate-900 flex items-center justify-between">
                    <span className="flex items-center">
                      <MapPin className="w-3.5 h-3.5 mr-1.5 text-brand-600" />
                      Route Map
                    </span>
                    <div className="flex items-center gap-1">
                      {/* Overview button */}
                      <button
                        onClick={() => {
                          setActiveCarId(null);
                          setViewingTransportId(null);
                          setEditingTransportId(null);
                        }}
                        className={`text-[10px] px-2 py-0.5 rounded-full flex items-center transition-colors ${
                          !activeCarId && !viewingTransportId && !editingTransportId
                            ? 'bg-brand-500 text-white'
                            : 'bg-steel-100 text-slate-600 hover:bg-steel-200'
                        }`}
                      >
                        Overview
                      </button>
                      {/* Active car indicator */}
                      {activeCarId && carsWithPassengers.find(c => c.carId === activeCarId) && (
                        <span className="text-[10px] px-2 py-0.5 bg-brand-100 text-brand-700 rounded-full flex items-center">
                          <Car className="w-3 h-3 mr-1" />
                          {carsWithPassengers.find(c => c.carId === activeCarId)?.car.plate_number}
                        </span>
                      )}
                      {/* Viewing/Editing transport indicator */}
                      {viewingTransportId && !editingTransportId && (
                        <span className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 rounded-full flex items-center">
                          <Eye className="w-3 h-3 mr-1" />
                          Viewing Travel
                        </span>
                      )}
                      {editingTransportId && (
                        <>
                          <span className="text-[10px] px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full flex items-center">
                            <Edit2 className="w-3 h-3 mr-1" />
                            Editing #{editingTransportId}
                          </span>
                          <button
                            onClick={() => {
                              // Cancel editing - clear assignments and reset state
                              const transport = localTransports.find(t => t.id === editingTransportId);
                              if (transport) {
                                const carId = transport.car;
                                const isAirport = transport.transport_type === 'airport_pickup' || transport.transport_type === 'airport_dropoff';
                                if (isAirport) {
                                  const tabKey = transport.transport_type === 'airport_pickup' ? 'pickups' : 'dropoffs';
                                  setCarAssignments(prev => ({
                                    ...prev,
                                    [tabKey]: {
                                      ...prev[tabKey],
                                      [carId]: []
                                    }
                                  }));
                                } else {
                                  const tabKey = transport.transport_type === 'daily_to_work' ? 'to_project' : 'to_accommodation';
                                  setDailyCarAssignments(prev => ({
                                    ...prev,
                                    [tabKey]: {
                                      ...prev[tabKey],
                                      [carId]: []
                                    }
                                  }));
                                }
                              }
                              setEditingTransportId(null);
                              setActiveCarId(null);
                            }}
                            className="text-[10px] px-2 py-0.5 bg-red-100 text-red-700 rounded-full flex items-center hover:bg-red-200"
                          >
                            <X className="w-3 h-3 mr-0.5" />
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </h2>
                </div>

                <div className="flex-1 min-h-0 relative">
                  {(() => {
                    const isPickup = airportSubTab === AIRPORT_SUB_TABS.PICKUPS;
                    const isDropoff = airportSubTab === AIRPORT_SUB_TABS.DROPOFFS;
                    const isAirportTransfer = mainTab === MAIN_TABS.AIRPORT_TRANSFERS;
                    const isOverviewMode = !activeCarId && !viewingTransportId;

                    // Get project coordinates as fallback center
                    const currentProject = projects.find(p => p.id === parseInt(selectedProject));
                    const projectCoords = currentProject?.latitude && currentProject?.longitude
                      ? { lat: parseFloat(currentProject.latitude), lng: parseFloat(currentProject.longitude) }
                      : null;

                    let airportMarkers = [];
                    let accommodationPoints = [];
                    let routeLinePoints = [];
                    let hasRealGeometry = false; // Track if we have actual road geometry vs straight lines

                    // VIEWING SAVED TRANSPORT MODE
                    if (viewingTransportId) {
                      const viewingTransport = localTransports.find(t => t.id === viewingTransportId);
                      if (viewingTransport) {
                        const isTransportPickup = viewingTransport.transport_type === 'airport_pickup';

                        // Check if transport has waypoints (multi-stop route)
                        if (viewingTransport.waypoints && Array.isArray(viewingTransport.waypoints) && viewingTransport.waypoints.length > 0) {
                          // Use waypoints for markers
                          viewingTransport.waypoints.forEach((wp, idx) => {
                            const lat = parseFloat(wp.lat);
                            const lng = parseFloat(wp.lon);
                            if (!isNaN(lat) && !isNaN(lng)) {
                              if (wp.type === 'airport') {
                                airportMarkers.push({
                                  code: wp.name?.match(/([A-Z]{3})/)?.[1] || wp.name,
                                  name: wp.name,
                                  lat, lng,
                                  userCount: wp.passenger_ids?.length || 0
                                });
                              } else if (wp.type === 'worksite') {
                                // Project marker - add to airportMarkers with isProject flag
                                airportMarkers.push({
                                  code: 'PROJECT',
                                  name: wp.name,
                                  lat, lng,
                                  userCount: wp.passenger_ids?.length || 0,
                                  isProject: true
                                });
                              } else {
                                // Accommodation marker
                                accommodationPoints.push({
                                  name: wp.name,
                                  lat, lng,
                                  passengers: viewingTransport.passengers_data?.filter(p =>
                                    wp.passenger_ids?.includes(p.id)
                                  ) || [],
                                  sequence: wp.sequence,
                                  isStart: idx === 0,
                                  isEnd: idx === viewingTransport.waypoints.length - 1,
                                  address: wp.address
                                });
                              }
                            }
                          });

                          // Use route_geometry if available, otherwise build from waypoints
                          if (viewingTransport.route_geometry && Array.isArray(viewingTransport.route_geometry) && viewingTransport.route_geometry.length > 2) {
                            routeLinePoints = viewingTransport.route_geometry;
                            hasRealGeometry = true;
                          } else {
                            // Fallback: straight lines between waypoints
                            viewingTransport.waypoints.forEach(wp => {
                              const lat = parseFloat(wp.lat);
                              const lng = parseFloat(wp.lon);
                              if (!isNaN(lat) && !isNaN(lng)) {
                                routeLinePoints.push([lat, lng]);
                              }
                            });
                          }
                        } else {
                          // Fallback: use origin/destination computed fields from serializer
                          const origin = viewingTransport.origin;
                          const destination = viewingTransport.destination;

                          // Add origin marker
                          if (origin?.lat && origin?.lon) {
                            const lat = parseFloat(origin.lat);
                            const lng = parseFloat(origin.lon);
                            if (!isNaN(lat) && !isNaN(lng)) {
                              if (origin.type === 'airport') {
                                airportMarkers.push({
                                  code: origin.name?.match(/([A-Z]{3})/)?.[1] || 'APT',
                                  name: origin.name,
                                  lat, lng,
                                  userCount: origin.passenger_ids?.length || 0
                                });
                              } else if (origin.type === 'worksite') {
                                airportMarkers.push({
                                  code: 'PROJECT',
                                  name: origin.name,
                                  lat, lng,
                                  userCount: 0,
                                  isProject: true
                                });
                              } else {
                                accommodationPoints.push({
                                  name: origin.name,
                                  lat, lng,
                                  passengers: [],
                                  sequence: 1,
                                  isStart: true,
                                  address: origin.address
                                });
                              }
                              routeLinePoints.push([lat, lng]);
                            }
                          }

                          // Add destination marker
                          if (destination?.lat && destination?.lon) {
                            const lat = parseFloat(destination.lat);
                            const lng = parseFloat(destination.lon);
                            if (!isNaN(lat) && !isNaN(lng)) {
                              if (destination.type === 'airport') {
                                airportMarkers.push({
                                  code: destination.name?.match(/([A-Z]{3})/)?.[1] || 'APT',
                                  name: destination.name,
                                  lat, lng,
                                  userCount: 0
                                });
                              } else if (destination.type === 'worksite') {
                                airportMarkers.push({
                                  code: 'PROJECT',
                                  name: destination.name,
                                  lat, lng,
                                  userCount: 0,
                                  isProject: true
                                });
                              } else {
                                accommodationPoints.push({
                                  name: destination.name,
                                  lat, lng,
                                  passengers: [],
                                  sequence: 2,
                                  isEnd: true,
                                  address: destination.address
                                });
                              }
                              routeLinePoints.push([lat, lng]);
                            }
                          }
                        }
                      }
                    } else if (isOverviewMode && !isAirportTransfer) {
                      // DAILY PLANNING OVERVIEW MODE: Show project and all accommodations
                      const pendingUsers = dailySubTab === DAILY_SUB_TABS.TO_PROJECT
                        ? pendingToProject
                        : pendingToAccommodation;

                      // Show project marker
                      if (projectCoords && currentProject) {
                        airportMarkers.push({
                          code: 'PROJECT',
                          name: currentProject.name,
                          lat: projectCoords.lat,
                          lng: projectCoords.lng,
                          userCount: pendingUsers.length,
                          isProject: true
                        });
                      }

                      // Show accommodations
                      const seenAccommodations = new Set();
                      pendingUsers.forEach(user => {
                        const accId = user.accommodation_id;
                        if (accId && !seenAccommodations.has(accId)) {
                          seenAccommodations.add(accId);
                          const acc = accommodations.find(a => a.id === accId);
                          if (acc && acc.latitude && acc.longitude) {
                            const lat = parseFloat(acc.latitude);
                            const lng = parseFloat(acc.longitude);
                            if (!isNaN(lat) && !isNaN(lng)) {
                              const usersAtAcc = pendingUsers.filter(u => u.accommodation_id === accId);
                              accommodationPoints.push({
                                name: user.accommodation_name,
                                lat,
                                lng,
                                passengers: usersAtAcc,
                                sequence: accommodationPoints.length + 1,
                                isOverview: true,
                                address: acc.address || user.address,
                                city: acc.city || user.city,
                                post_code: acc.post_code || user.post_code,
                                country: acc.country || user.country
                              });
                            }
                          } else if (user.latitude && user.longitude) {
                            // Use coordinates from pending data
                            const lat = parseFloat(user.latitude);
                            const lng = parseFloat(user.longitude);
                            if (!isNaN(lat) && !isNaN(lng)) {
                              const usersAtAcc = pendingUsers.filter(u => u.accommodation_id === accId);
                              accommodationPoints.push({
                                name: user.accommodation_name,
                                lat,
                                lng,
                                passengers: usersAtAcc,
                                sequence: accommodationPoints.length + 1,
                                isOverview: true,
                                address: user.address,
                                city: user.city,
                                post_code: user.post_code,
                                country: user.country
                              });
                            }
                          }
                        }
                      });

                    } else if (isOverviewMode && isAirportTransfer) {
                      // AIRPORT TRANSFER OVERVIEW MODE: Show all airports and all user accommodations
                      const pendingUsers = isPickup ? pendingPickups : pendingDropoffs;

                      // Collect all unique airports
                      const airportField = isPickup ? 'arrival_airport' : 'departure_airport';
                      const uniqueAirports = [...new Set(pendingUsers.map(u => u[airportField]).filter(Boolean))];

                      uniqueAirports.forEach(code => {
                        const coords = getAirportCoordinates(code);
                        if (coords) {
                          const usersAtAirport = pendingUsers.filter(u => u[airportField] === code);
                          airportMarkers.push({
                            code,
                            name: coords.name,
                            lat: coords.lat,
                            lng: coords.lng,
                            userCount: usersAtAirport.length
                          });
                        }
                      });

                      // Collect all unique accommodations for pending users
                      const seenAccommodations = new Set();
                      pendingUsers.forEach(user => {
                        const userId = user.user_id || user.id;
                        const accInfo = userAccommodations[userId];
                        if (accInfo && accInfo.accommodation_id && !seenAccommodations.has(accInfo.accommodation_id)) {
                          seenAccommodations.add(accInfo.accommodation_id);
                          const acc = accommodations.find(a => a.id === accInfo.accommodation_id);
                          if (acc && acc.latitude && acc.longitude) {
                            const lat = parseFloat(acc.latitude);
                            const lng = parseFloat(acc.longitude);
                            if (!isNaN(lat) && !isNaN(lng)) {
                              // Count users at this accommodation
                              const usersAtAcc = pendingUsers.filter(u => {
                                const uid = u.user_id || u.id;
                                return userAccommodations[uid]?.accommodation_id === accInfo.accommodation_id;
                              });
                              accommodationPoints.push({
                                name: accInfo.accommodation_name,
                                lat,
                                lng,
                                passengers: usersAtAcc,
                                sequence: accommodationPoints.length + 1,
                                isOverview: true,
                                address: acc.address,
                                city: acc.city,
                                post_code: acc.post_code,
                                country: acc.country
                              });
                            }
                          }
                        }
                      });

                    } else if (activeCarId && isAirportTransfer) {
                      // AIRPORT CAR MODE: Show only selected car's route
                      const activeCar = carsWithPassengers.find(c => c.carId === activeCarId);

                      if (activeCar) {
                        // Get airport for this car
                        if (isPickup && activeCar.airportInfo.arrivalAirportCoords) {
                          const coords = activeCar.airportInfo.arrivalAirportCoords;
                          airportMarkers.push({
                            code: activeCar.airportInfo.arrivalAirport,
                            name: coords.name,
                            lat: coords.lat,
                            lng: coords.lng,
                            userCount: activeCar.passengers.length
                          });
                        } else if (isDropoff && activeCar.airportInfo.departureAirportCoords) {
                          const coords = activeCar.airportInfo.departureAirportCoords;
                          airportMarkers.push({
                            code: activeCar.airportInfo.departureAirport,
                            name: coords.name,
                            lat: coords.lat,
                            lng: coords.lng,
                            userCount: activeCar.passengers.length
                          });
                        }

                        // Get accommodations for this car
                        Object.entries(activeCar.passengersByAccommodation).forEach(([accName, accData], idx) => {
                          const acc = accommodations.find(a => a.id === accData.accommodation_id);
                          if (acc && acc.latitude && acc.longitude) {
                            const lat = parseFloat(acc.latitude);
                            const lng = parseFloat(acc.longitude);
                            if (!isNaN(lat) && !isNaN(lng)) {
                              accommodationPoints.push({
                                name: accName,
                                lat,
                                lng,
                                passengers: accData.passengers,
                                sequence: idx + 1,
                                isDriverAccommodation: accData.isDriverAccommodation,
                                address: acc.address,
                                city: acc.city,
                                post_code: acc.post_code,
                                country: acc.country
                              });
                            }
                          }
                        });

                        // Build route line for car mode
                        if (airportMarkers.length > 0 && accommodationPoints.length > 0) {
                          if (isPickup) {
                            routeLinePoints.push([airportMarkers[0].lat, airportMarkers[0].lng]);
                            accommodationPoints.forEach(p => routeLinePoints.push([p.lat, p.lng]));
                          } else {
                            accommodationPoints.forEach(p => routeLinePoints.push([p.lat, p.lng]));
                            routeLinePoints.push([airportMarkers[0].lat, airportMarkers[0].lng]);
                          }
                        }
                      }
                    } else if (activeCarId && !isAirportTransfer) {
                      // DAILY PLANNING CAR MODE: Show selected car's route to/from project
                      const tabKey = getDailyTabKey();
                      const carPassengers = dailyCarAssignments[tabKey]?.[activeCarId] || [];
                      const isToProject = dailySubTab === DAILY_SUB_TABS.TO_PROJECT;

                      // Show project marker
                      if (projectCoords && currentProject) {
                        airportMarkers.push({
                          code: 'PROJECT',
                          name: currentProject.name,
                          lat: projectCoords.lat,
                          lng: projectCoords.lng,
                          userCount: carPassengers.length,
                          isProject: true
                        });
                      }

                      // Group passengers by accommodation and show markers
                      const passengersByAcc = {};
                      carPassengers.forEach(p => {
                        const accName = p.accommodation_name || 'No accommodation';
                        const accId = p.accommodation_id;
                        if (!passengersByAcc[accName]) {
                          passengersByAcc[accName] = { accommodation_id: accId, passengers: [] };
                        }
                        passengersByAcc[accName].passengers.push(p);
                      });

                      Object.entries(passengersByAcc).forEach(([accName, accData], idx) => {
                        const acc = accommodations.find(a => a.id === accData.accommodation_id);
                        // Try to get coordinates from accommodation or from passenger data
                        let lat = null, lng = null;
                        if (acc && acc.latitude && acc.longitude) {
                          lat = parseFloat(acc.latitude);
                          lng = parseFloat(acc.longitude);
                        } else if (accData.passengers[0]?.latitude && accData.passengers[0]?.longitude) {
                          lat = parseFloat(accData.passengers[0].latitude);
                          lng = parseFloat(accData.passengers[0].longitude);
                        }

                        if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
                          accommodationPoints.push({
                            name: accName,
                            lat,
                            lng,
                            passengers: accData.passengers,
                            sequence: idx + 1,
                            address: acc?.address || accData.passengers[0]?.address,
                            city: acc?.city || accData.passengers[0]?.city,
                            post_code: acc?.post_code || accData.passengers[0]?.post_code,
                            country: acc?.country || accData.passengers[0]?.country
                          });
                        }
                      });

                      // Build route line
                      if (airportMarkers.length > 0 && accommodationPoints.length > 0) {
                        if (isToProject) {
                          // Accommodation → Project
                          accommodationPoints.forEach(p => routeLinePoints.push([p.lat, p.lng]));
                          routeLinePoints.push([airportMarkers[0].lat, airportMarkers[0].lng]);
                        } else {
                          // Project → Accommodation
                          routeLinePoints.push([airportMarkers[0].lat, airportMarkers[0].lng]);
                          accommodationPoints.forEach(p => routeLinePoints.push([p.lat, p.lng]));
                        }
                      }
                    }

                    // Calculate map bounds
                    const allPositions = [];
                    airportMarkers.forEach(a => allPositions.push([a.lat, a.lng]));
                    accommodationPoints.forEach(p => allPositions.push([p.lat, p.lng]));
                    if (projectCoords && allPositions.length === 0) {
                      allPositions.push([projectCoords.lat, projectCoords.lng]);
                    }

                    const hasMapData = allPositions.length > 0;
                    const mapCenter = hasMapData
                      ? allPositions[0]
                      : projectCoords
                        ? [projectCoords.lat, projectCoords.lng]
                        : [52.2297, 21.0122]; // Default: Warsaw

                    return hasMapData || projectCoords ? (
                      <MapContainer
                        key={`route-map-${selectedProject}-${activeCarId || 'overview'}-${accommodationPoints.length}-${airportMarkers.length}`}
                        center={mapCenter}
                        zoom={11}
                        style={{ width: '100%', height: '100%' }}
                        scrollWheelZoom={true}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />

                        {allPositions.length > 0 && <MapFitBounds positions={allPositions} />}

                        {/* Airport/Project markers */}
                        {airportMarkers.map((airport, idx) => (
                          <Marker
                            key={`airport-${idx}`}
                            position={[airport.lat, airport.lng]}
                            icon={airport.isProject ? createProjectIcon() : createAirportIcon()}
                          >
                            <Popup>
                              <div className="text-sm">
                                <div className={`font-bold ${airport.isProject ? 'text-green-700' : 'text-brand-700'}`}>
                                  {airport.isProject ? airport.name : airport.code}
                                </div>
                                {!airport.isProject && <div className="text-xs text-slate-600">{airport.name}</div>}
                                <div className="text-xs text-slate-400 mt-1">
                                  {airport.isProject
                                    ? `Work site - ${airport.userCount} user(s)`
                                    : isOverviewMode
                                      ? `${airport.userCount} user(s)`
                                      : (isPickup ? 'Pickup location' : 'Dropoff destination')}
                                </div>
                              </div>
                            </Popup>
                          </Marker>
                        ))}

                        {/* Accommodation markers */}
                        {accommodationPoints.map((point, idx) => (
                          <Marker
                            key={`acc-${idx}`}
                            position={[point.lat, point.lng]}
                            icon={createAccommodationIcon(point.isOverview ? null : point.sequence, !point.isOverview)}
                          >
                            <Popup>
                              <div className="text-sm">
                                <div className={`font-bold ${point.isDriverAccommodation ? 'text-green-700' : 'text-purple-700'}`}>
                                  {point.name}
                                  {point.isDriverAccommodation && ' (Driver)'}
                                </div>
                                {/* Full address display */}
                                {(point.address || point.city || point.post_code || point.country) && (
                                  <div className="text-xs text-slate-600 mt-1">
                                    {[point.address, point.city, point.post_code, point.country]
                                      .filter(Boolean)
                                      .join(', ')}
                                  </div>
                                )}
                                <div className="text-xs text-slate-500 mt-1">
                                  {point.isOverview
                                    ? `${point.passengers.length} user(s)`
                                    : `Stop #${point.sequence} • ${point.passengers.length} passenger(s)`}
                                </div>
                                <div className="text-xs text-slate-400 mt-1">
                                  {point.passengers.map(p => p.first_name || p.user_name).join(', ')}
                                </div>
                              </div>
                            </Popup>
                          </Marker>
                        ))}

                        {/* Route polyline - use optimized geometry with colors per segment */}
                        {(() => {
                          // Get route data from appropriate source (airport or daily)
                          let routeData = null;
                          if (isAirportTransfer && activeCarId) {
                            const tabKey = airportSubTab;
                            routeData = optimizedRoutes[tabKey]?.[activeCarId];
                          } else if (!isAirportTransfer && activeCarId) {
                            const tabKey = getDailyTabKey();
                            routeData = dailyOptimizedRoutes[tabKey]?.[activeCarId];
                          }

                          const segments = routeData?.segments;

                          // Color palette for segments (distinct, visible colors)
                          const segmentColors = [
                            '#2563eb', // blue
                            '#059669', // green
                            '#7c3aed', // purple
                            '#ea580c', // orange
                            '#dc2626', // red
                            '#0891b2', // cyan
                            '#ca8a04', // yellow
                            '#be185d', // pink
                          ];

                          if (segments && segments.length > 0) {
                            // Draw each segment with different color
                            return segments.map((segment, idx) => {
                              const geometry = segment.geometry;
                              if (!geometry || geometry.length < 2) return null;

                              const color = segmentColors[idx % segmentColors.length];

                              return (
                                <Polyline
                                  key={`segment-${idx}`}
                                  positions={geometry}
                                  pathOptions={{
                                    color: color,
                                    weight: 5,
                                    opacity: 0.9
                                  }}
                                />
                              );
                            });
                          } else if (routeData?.geometry && routeData.geometry.length > 1) {
                            // Fallback to full geometry (single color)
                            return (
                              <Polyline
                                positions={routeData.geometry}
                                pathOptions={{
                                  color: isAirportTransfer ? (isPickup ? '#2563eb' : '#ea580c') : '#059669',
                                  weight: 5,
                                  opacity: 0.9
                                }}
                              />
                            );
                          } else if (routeLinePoints.length > 1) {
                            // Use solid line for real road geometry, dashed for straight-line fallback
                            return (
                              <Polyline
                                positions={routeLinePoints}
                                pathOptions={{
                                  color: isAirportTransfer ? (isPickup ? '#2563eb' : '#ea580c') : '#059669',
                                  weight: hasRealGeometry ? 5 : 4,
                                  opacity: hasRealGeometry ? 0.9 : 0.7,
                                  ...(hasRealGeometry ? {} : { dashArray: '10, 6' })
                                }}
                              />
                            );
                          }
                          return null;
                        })()}
                      </MapContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-400">
                        <div className="text-center p-4">
                          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Drag people to cars to see route</p>
                          <p className="text-xs mt-1 text-slate-300">
                            Map will show accommodations and planned route
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
        </div>
    </DndProvider>
  );
};

export default DailyPlanning1Day;
